import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faFilter,
  faWandMagicSparkles,
  faUserGraduate,
  faClipboardCheck,
  faBuildingColumns,
  faGraduationCap,
  faAward,
  faPeopleGroup,
  faChartBar,
  faTableList,
  faTurnUp,
  faFileCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import './ReviewDashboard.scss';
import PalliPaarvaiDetail from './PalliPaarvaiDetail';
import {
  useGetReviewDashboardQuery,
  useGetReviewDashboard1Query,
  useGetReviewEnrollmentTableQuery,
  useGetReviewAttendanceTableQuery,
  useGetReviewInfraTableQuery,
  useGetReviewScholarshipTableQuery,
  useGetReviewThiranTableQuery,
  useGetReviewSmcTableQuery,
  useGetAcademicKpisQuery,
  useGetAcademicDetailTableQuery,
} from '../Dashboard/Dashboard.service';
import { group } from 'console';

/**
 * State Monitoring & Review — renders INSIDE the School Dashboard page as the
 * "Review Dashboard" nav tab (the shared DashboardHeader is the parent's).
 *
 * UI is built with PrimeReact: DataTable (+ built-in paginator & sorting),
 * Button, SelectButton, Accordion, Dropdown, MultiSelect.
 *
 * Every card has the SAME 3-level drill-down: District → Block → School.
 * Columns for each level come from `detailSchemas[card.title]`.
 *
 * DATA: all values are DUMMY (deterministic). Replace `makeBlocks`,
 * `makeSchools`, `detailCell`, `blockCell` and `schoolVal` with API calls later.
 */

type Dot = 'red' | 'orange' | 'green' | 'gray';
type Rag = 'critical' | 'watch' | 'good' | 'none';

interface KpiRow {
  label: string;
  value?: string;
  split?: { good: string; bad: string };
  tone?: 'pos' | 'neg' | 'warn';
  dot?: Dot;
  /** reviewdashboard KPI key this row's value comes from (when live). */
  apiKey?: string;
  /** For split rows: the API value is the "bad" side (e.g. not-marked %). */
  apiValueIsBad?: boolean;
}

interface ReviewCard {
  title: string;
  color: string;
  short?: string;
  icon: string | any;
  rows: KpiRow[];
  assessmentToggle?: boolean;
}

interface KpiOption {
  label: string;
  value: string;
}

const reviewCards: ReviewCard[] = [
  // {
  //   title: 'State Summary',
  //   color: '#2f6fdb',
  //   icon: faChartBar,
  //   rows: [
  //     { label: 'Schools', value: '59,312' },
  //     { label: 'Students', value: '1,21,92,051' },
  //     { label: 'Teachers', value: '5,71,185' },
  //     {
  //       label: 'Student attendance %',
  //       split: { good: '89%', bad: '11%' },
  //       dot: 'red',
  //     },
  //     {
  //       label: 'Teacher attendance %',
  //       split: { good: '93.2%', bad: '6.8%' },
  //       dot: 'red',
  //     },
  //     { label: 'Potential dropouts (15 days absent)', value: '1,87,162' },
  //     { label: 'Schools with critical infra gap', value: '21,122' },
  //   ],
  // },
  // Card order for now: Palli Paarvai, Enrollment, Attendance. The other
  // cards below are commented out for now — uncomment a block to bring it
  // back into the Review Dashboard.
  {
    title: 'Palli Paarvai',
    color: '#2563eb',
    icon: faClipboardCheck,
    rows: [
      {
        label: '% of Class Observation',
        value: '65.0%',
        dot: 'red',
        apiKey: 'pct_class_observation',
      },
      {
        label: '% of Schools Not Observed (term)',
        value: '+35.0%',
        tone: 'neg',
        dot: 'red',
        apiKey: 'pct_schools_not_observed',
      },
      {
        label: '% of Officials Not Observed (month)',
        value: '+18.0%',
        tone: 'warn',
        dot: 'orange',
        apiKey: 'pct_officials_not_observed',
      },
      {
        label: '% of Schools Observed 3+ Times',
        value: '56.8%',
        dot: 'orange',
        apiKey: 'pct_observed_3plus',
      },
      {
        label: 'Low performers (observation < 75%)',
        value: '136',
        apiKey: 'low_performers',
      },
      {
        label: 'Top performers (observation ≥ 75%)',
        value: '16',
        apiKey: 'top_performers',
      },
    ],
  },
  {
    title: 'Enrollment',
    color: '#0f9d8f',
    icon: faUserGraduate,
    rows: [
      {
        label: 'Zero Enrollment Schools',
        value: '1,509',
        apiKey: 'zero_enrolment_schools',
      },
      {
        label: 'Zero Teacher Schools',
        value: '410',
        apiKey: 'zero_teacher_schools',
      },
      {
        label: 'Single Teacher Schools',
        value: '4,598',
        apiKey: 'single_teacher_schools',
      },
      {
        label: 'Under 10 Students Schools',
        value: '3,169',
        apiKey: 'under_10_students_schools',
      },
      {
        label: 'PTR over 60 Schools',
        value: '478',
        apiKey: 'ptr_over_60_schools',
      },
      {
        label: 'Enrollment increase schools',
        value: '16,505',
        apiKey: 'enrolment_increase_schools',
      },
      {
        label: 'Schools with declined enrollment',
        value: '37,980',
        apiKey: 'declined_enrolment_schools',
      },
      {
        label: 'Transition rate - % schools not meeting 100%',
        value: '+63.7%',
        dot: 'red',
        apiKey: 'transition_not_meeting_100_pct',
      },
    ],
  },
  {
    title: 'Attendance',
    color: '#f59e0b',
    icon: faClipboardCheck,
    rows: [
      {
        label: 'Student attendance %',
        split: { good: '89%', bad: '11%' },
        // dot: 'orange',
        apiKey: 'student_attendance_pct',
      },
      {
        label: 'Teachers attendance %',
        split: { good: '93.2%', bad: '6.8%' },
        // dot: 'red',
        apiKey: 'teacher_attendance_pct',
      },
      {
        label: 'Schools not marked attendance %',
        split: { good: '82.9%', bad: '17.1%' },
        // dot: 'orange',
        apiKey: 'school_non_mark_pct',
        apiValueIsBad: true,
      },
      {
        label: 'Schools below 60% student attendance',
        apiKey: 'schools_student_below_60',
      },
      {
        label: 'Schools below 60% teacher attendance',
        apiKey: 'schools_teacher_below_60',
      },
      {
        label: 'Potential dropout (15days absent)',
        apiKey: 'potential_dropouts',
      },
      {
        label: 'Teacher long absent (30+ days)',
        apiKey: 'long_absent',
      },
    ],
  },
  {
    title: 'Infrastructure',
    color: '#3b82f6',
    icon: faBuildingColumns,
    rows: [
      {
        label: 'Schools with critical gaps',
        value: '21,122',
        apiKey: 'critical_gap_schools',
      },
      { label: 'Zero Classroom', value: '—', apiKey: 'zero_classroom' },
      {
        label: 'Zero Toilet school',
        value: '5,154',
        apiKey: 'zero_toilet_schools',
      },
      {
        label: 'No Drinking Water school',
        value: '9,094',
        apiKey: 'no_drinking_water_schools',
      },
      { label: 'No EB Connection', value: '—', apiKey: 'eb_yn' },
      {
        label: 'Building to be demolished',
        value: '—',
        apiKey: 'build_tobe_demolish',
      },
      {
        label: 'School has no compound wall',
        value: '—',
        apiKey: 'compound_wall_yn',
      },
      { label: 'No kitchen shed', value: '—', apiKey: 'kitchen_shed_yn' },
    ],
  },
  {
    title: 'Academic Performance',
    color: '#ec4899',
    short: 'Academic',
    icon: faGraduationCap,
    assessmentToggle: true,
    rows: [
      {
        label: 'Pass Percentage %',
        value: '86.7%',
        dot: 'green',
        apiKey: 'Pass Percentage %',
      },
      {
        label: 'Average Mark %',
        value: '32.0%',
        dot: 'red',
        apiKey: 'Average Mark',
      },
      {
        label: 'Year-on-Year Improvement %',
        value: '3.8%',
        dot: 'orange',
        apiKey: 'YoY Improvement',
      },
      {
        label: 'Exam-to-Exam Improvement (PP) %',
        value: '3.8%',
        dot: 'orange',
        apiKey: 'Exam to Exam Pass Point',
      },
      {
        label: 'Assessment Coverage %',
        value: '86.5%',
        dot: 'green',
        apiKey: 'Assessment Coverage',
      },
      {
        label: 'Mark Entry Completion %',
        value: '2.4%',
        dot: 'red',
        apiKey: 'Mark Entry Completion',
      },
    ],
  },
  {
    title: 'THIRAN',
    color: '#84cc16',
    icon: faUserGraduate,
    rows: [
      {
        label: 'THIRAN students identified',
        value: '-',
        apiKey: 'thiran_students_identified',
      },
      {
        label: 'Schools covered',
        value: '13,229',
        apiKey: 'schools_covered',
      },
      {
        label: '% of enrolment',
        value: '46.8%',
        dot: 'green',
        apiKey: 'pct_of_enrolment',
      },
      {
        label: 'BLO attainment',
        value: '94.0%',
        dot: 'green',
        apiKey: 'blo_attainment_pct',
      },
      {
        label: 'Baseline update %',
        value: '52.8%',
        dot: 'red',
        apiKey: 'baseline_update_pct',
      },
    ],
  },
  {
    title: 'Scholarship',
    color: '#c026d3',
    icon: faAward,
    rows: [
      {
        label: 'Eligible students',
        value: '-',
        apiKey: 'eligible_students',
      },
      {
        label: 'Payment success %',
        value: '0%',
        dot: 'red',
        apiKey: 'payment_success_pct',
      },
      {
        label: 'Payment failed',
        value: '-',
        dot: 'orange',
        apiKey: 'payment_failed',
      },
      {
        label: 'Payment pending',
        value: '-',
        dot: 'orange',
        apiKey: 'payment_pending',
      },
      {
        label: 'NPCI inactive',
        value: '-',
        apiKey: 'npci_inactive',
      },
      {
        label: 'Aadhaar not updated',
        value: '-',
        apiKey: 'aadhar_not_updated',
      },
    ],
  },
  {
    title: 'SMC',
    color: '#ea580c',
    icon: faPeopleGroup,
    rows: [
      {
        label: 'SMC resolutions raised',
        value: '1,44,715',
        apiKey: 'res_count',
      },
      {
        label: 'Resolutions Closed',
        value: '56,486',
        apiKey: 'resolved_cnt',
      },
      {
        label: 'Pending resolutions',
        value: '88,229',
        apiKey: 'pending_cnt',
      },
      {
        label: 'Closure rate %',
        value: '42.3%',
        dot: 'orange',
        apiKey: 'closure_rate',
      },
      {
        label: 'Emergency resolutions not closed',
        value: '19,375',
        dot: 'orange',
        apiKey: 'emergency_cnt',
      },
    ],
  },
  // {
  //   title: 'Recognition Order - DPS',
  //   color: '#7c3aed',
  //   icon: faFileCircleCheck,
  //   rows: [
  //     {
  //       label: 'Total pending applications',
  //       value: '745',
  //       dot: 'orange',
  //     },
  //     {
  //       label: 'District pending',
  //       value: '453',
  //       dot: 'orange',
  //     },
  //     {
  //       label: 'State pending',
  //       value: '292',
  //       dot: 'red',
  //     },
  //     {
  //       label: 'Pending < 15 days',
  //       value: '725',
  //       dot: 'green',
  //     },
  //     {
  //       label: 'Pending 15-30 days',
  //       value: '9',
  //       dot: 'orange',
  //     },
  //     {
  //       label: 'Pending > 30 days',
  //       value: '11',
  //       dot: 'red',
  //     },
  //   ],
  // },
];

const DISTRICTS = [
  'Chennai',
  'Coimbatore',
  'Madurai',
  'Salem',
  'Tiruchirappalli',
  'Tirunelveli',
  'Tiruppur',
  'Erode',
  'Vellore',
  'Thoothukkudi',
  'Dindigul',
  'Thanjavur',
  'Kanchipuram',
  'Cuddalore',
  'Tiruvallur',
  'Villupuram',
  'Virudhunagar',
  'Sivaganga',
  'Nagapattinam',
  'Namakkal',
  'Dharmapuri',
  'Krishnagiri',
  'Karur',
  'Pudukkottai',
  'Ramanathapuram',
  'Theni',
  'Tiruvannamalai',
  'Tiruvarur',
  'Ariyalur',
  'Perambalur',
  'The Nilgiris',
  'Kanniyakumari',
  'Tenkasi',
  'Kallakurichi',
  'Chengalpattu',
  'Ranipet',
  'Tirupathur',
  'Mayiladuthurai',
];

// ── Per-component table schemas (drive District, Block AND School KPI columns) ──
type ColKind = 'num' | 'ratio' | 'pct' | 'posneg' | 'text';
type ColTone =
  | 'none'
  | 'blue'
  | 'green'
  | 'greenbg'
  | 'orangebg'
  | 'redbg'
  | 'pctHi'
  | 'pctLo';
interface DCol {
  label: string;
  kind: ColKind;
  tone?: ColTone;
  total: string;
  text?: string;
  /** reviewinfratable (etc.) item field this column reads when live. */
  apiKey?: string;
}

const detailSchemas: Record<string, DCol[]> = {
  'State Summary': [
    { label: 'Schools', kind: 'num', total: '59,312' },
    { label: 'Students', kind: 'num', tone: 'blue', total: '1,21,92,051' },
    { label: 'Teachers', kind: 'num', total: '5,71,185' },
    { label: 'Student attendance %', kind: 'pct', tone: 'pctHi', total: '89%' },
    {
      label: 'Teacher attendance %',
      kind: 'pct',
      tone: 'pctHi',
      total: '93.2%',
    },
    {
      label: 'Potential dropouts (15 days absent)',
      kind: 'num',
      tone: 'orangebg',
      total: '1,87,162',
    },
    {
      label: 'Schools with critical infra gap',
      kind: 'num',
      tone: 'redbg',
      total: '21,122',
    },
  ],
  Enrollment: [
    {
      label: 'Zero Enrollment Schools',
      kind: 'num',
      total: '1,509',
      tone: 'orangebg',
      apiKey: 'zero_enrolment_schools',
    },
    {
      label: 'Zero Teacher Schools',
      kind: 'num',
      tone: 'orangebg',
      total: '410',
      apiKey: 'zero_teacher_schools',
    },
    {
      label: 'Single Teacher Schools',
      kind: 'num',
      tone: 'orangebg',
      total: '4,598',
      apiKey: 'single_teacher_schools',
    },
    {
      label: 'Under 10 Students Schools',
      kind: 'num',
      tone: 'orangebg',
      total: '3,169',
      apiKey: 'under_10_students_schools',
    },
    {
      label: 'PTR over 60 Schools',
      kind: 'num',
      tone: 'orangebg',
      total: '478',
      apiKey: 'ptr_over_60_schools',
    },
    {
      label: 'Enrollment increase schools',
      kind: 'num',
      tone: 'greenbg',
      total: '16,505',
      apiKey: 'enrolment_increase_schools',
    },
    {
      label: 'Schools with declined enrollment',
      kind: 'num',
      tone: 'orangebg',
      total: '37,980',
      apiKey: 'declined_enrolment_schools',
    },
    {
      label: 'Transition rate - % schools not meeting 100%',
      kind: 'pct',
      tone: 'pctLo',
      total: '63.7%',
      apiKey: 'transition_not_meeting_100_pct',
    },
  ],
  Attendance: [
    {
      label: 'Student attendance %',
      kind: 'pct',
      tone: 'pctHi',
      total: '89%',
      apiKey: 'student_attendance_pct',
    },
    {
      label: 'Teachers attendance %',
      kind: 'pct',
      tone: 'pctHi',
      total: '93.2%',
      apiKey: 'teacher_attendance_pct',
    },
    {
      label: 'Schools not marked attendance %',
      kind: 'pct',
      tone: 'pctHi',
      total: '17.1%',
      apiKey: 'school_non_mark_pct',
    },
    {
      label: 'Schools below 60% student attendance',
      kind: 'num',
      tone: 'redbg',
      total: '0',
      apiKey: 'schools_student_below_60',
    },
    {
      label: 'Schools below 60% teacher attendance',
      kind: 'num',
      tone: 'redbg',
      total: '0',
      apiKey: 'schools_teacher_below_60',
    },
    {
      label: 'Potential dropout (15days absent)',
      kind: 'num',
      tone: 'orangebg',
      total: '-',
      apiKey: 'potential_dropouts',
    },
    {
      label: 'Teacher long absent (30+ days)',
      kind: 'num',
      tone: 'orangebg',
      total: '-',
      apiKey: 'teacher_long_absent',
    },
  ],
  Infrastructure: [
    {
      label: 'Schools with critical gaps',
      kind: 'num',
      tone: 'redbg',
      total: '21,122',
      apiKey: 'critical_gap_schools',
    },
    {
      label: 'Zero Classroom',
      kind: 'num',
      tone: 'pctHi',
      total: '0',
      apiKey: 'zero_classroom',
    },
    {
      label: 'Zero Toilet school',
      kind: 'num',
      tone: 'redbg',
      total: '5154',
      apiKey: 'zero_toilet_schools',
    },
    {
      label: 'No Drinking Water school',
      kind: 'num',
      tone: 'redbg',
      total: '9094',
      apiKey: 'no_drinking_water_schools',
    },
    {
      label: 'No EB Connection',
      kind: 'num',
      tone: 'redbg',
      total: '1146',
      apiKey: 'no_eb_connection',
    },
    {
      label: 'Building to be demolished',
      kind: 'num',
      tone: 'redbg',
      total: '2276',
      apiKey: 'building_to_be_demolished',
    },
    {
      label: 'School has no compound wall',
      kind: 'num',
      tone: 'pctHi',
      total: '0',
      apiKey: 'no_compound_wall',
    },
    // {
    //   label: 'School needing any infra facility',
    //   kind: 'num',
    //   tone: 'pctHi',
    //   total: '0',
    //   apiKey: 'needing_any_infra',
    // },
    {
      label: 'No kitchen shed',
      kind: 'num',
      tone: 'redbg',
      total: '2366',
      apiKey: 'no_kitchen_shed',
    },
  ],
  'Academic Performance': [
    {
      label: 'Pass Percentage %',
      kind: 'pct',
      tone: 'pctHi',
      total: '83.9%',
      apiKey: 'pass_pct',
    },
    {
      label: 'Average Mark',
      kind: 'pct',
      tone: 'pctHi',
      total: '31.9%',
      apiKey: 'average_mark',
    },
    {
      label: 'Year-on-Year Improvement %',
      kind: 'pct',
      tone: 'pctHi',
      total: '2.5%',
      apiKey: 'yoy_improvement',
    },
    {
      label: 'Exam-to-Exam Improvement (PP)',
      kind: 'pct',
      tone: 'pctHi',
      total: '2.5%',
      apiKey: 'exam_to_exam_pp',
    },
    {
      label: 'Assessment Coverage %',
      kind: 'pct',
      tone: 'pctHi',
      total: '86.5%',
      apiKey: 'assessment_coverage',
    },
    {
      label: 'Mark Entry Completion %',
      kind: 'pct',
      tone: 'pctHi',
      total: '2.4%',
      apiKey: 'mark_entry_completion',
    },
  ],
  THIRAN: [
    {
      label: 'THIRAN Students identified',
      kind: 'num',
      total: '7,43,927',
      apiKey: 'thiran_students_identified',
    },
    {
      label: 'Schools covered',
      kind: 'num',
      total: '13,229',
      apiKey: 'schools_covered',
    },
    {
      label: '% of enrolment',
      kind: 'pct',
      tone: 'pctHi',
      total: '46.8%',
      apiKey: 'pct_of_enrolment',
    },
    {
      label: 'BLO attainment %',
      kind: 'pct',
      tone: 'greenbg',
      total: '94%',
      apiKey: 'blo_attainment_pct',
    },
    {
      label: 'Baseline update %',
      kind: 'pct',
      tone: 'pctHi',
      total: '52.8%',
      apiKey: 'baseline_update_pct',
    },
  ],
  Scholarship: [
    {
      label: 'Total Students',
      kind: 'num',
      total: '12,205,874',
      apiKey: 'total_students',
    },
    {
      label: 'Eligible Students',
      kind: 'num',
      total: '1,960,414',
      apiKey: 'eligible_students',
    },
    {
      label: 'Payment Success',
      kind: 'num',
      tone: 'greenbg',
      total: '0',
      apiKey: 'payment_success',
    },
    {
      label: 'Payment Success %',
      kind: 'pct',
      tone: 'pctHi',
      total: '0.0%',
      apiKey: 'payment_success_pct',
    },
    {
      label: 'Payment Failed',
      kind: 'num',
      tone: 'redbg',
      total: '0',
      apiKey: 'payment_failed',
    },
    {
      label: 'Payment Failed %',
      kind: 'pct',
      tone: 'pctHi',
      total: '0.0%',
      apiKey: 'payment_failed_pct',
    },
    {
      label: 'Payment Pending',
      kind: 'num',
      tone: 'orangebg',
      total: '1,960,414',
      apiKey: 'payment_pending',
    },
    {
      label: 'Payment Pending %',
      kind: 'pct',
      tone: 'pctHi',
      total: '100.0%',
      apiKey: 'payment_pending_pct',
    },
    {
      label: 'NPCI Inactive',
      kind: 'num',
      tone: 'redbg',
      total: '506,254',
      apiKey: 'npci_inactive',
    },
    {
      label: 'Aadhaar Not Updated',
      kind: 'num',
      tone: 'redbg',
      total: '32,002',
      apiKey: 'aadhar_not_updated',
    },
  ],
  SMC: [
    {
      label: 'SMC resolutions raised',
      kind: 'num',
      total: '1,44,715',
      apiKey: 'res_count',
    },
    {
      label: 'Resolutions Closed',
      kind: 'num',
      tone: 'pctHi',
      total: '56,486',
      apiKey: 'resolved_cnt',
    },
    {
      label: 'Pending resolutions',
      kind: 'num',
      tone: 'orangebg',
      total: '88,229',
      apiKey: 'pending_cnt',
    },
    {
      label: 'Closure rate %',
      kind: 'pct',
      tone: 'pctHi',
      total: '42.3%',
      apiKey: 'closure_rate',
    },
    {
      label: 'Emergency resolutions not closed',
      kind: 'num',
      tone: 'redbg',
      total: '19,375',
      apiKey: 'emergency_cnt',
    },
  ],
  'Palli Paarvai': [
    {
      label: '% of Class Observation',
      kind: 'pct',
      tone: 'pctHi',
      total: '65.0%',
    },
    {
      label: '% of Schools Not Observed (term)',
      kind: 'pct',
      tone: 'pctLo',
      total: '35.0%',
    },
    {
      label: '% of Officials Not Observed (month)',
      kind: 'pct',
      tone: 'pctLo',
      total: '18.0%',
    },
    {
      label: '% of Schools Observed 3+ Times',
      kind: 'pct',
      tone: 'pctHi',
      total: '56.8%',
    },
    {
      label: 'Low performers (observation < 75%)',
      kind: 'num',
      tone: 'redbg',
      total: '136',
    },
    {
      label: 'Top performers (observation ≥ 75%)',
      kind: 'num',
      tone: 'green',
      total: '16',
    },
  ],
  'Recognition Order - DPS': [
    {
      label: 'DISTRICT',
      kind: 'text',
      total: 'District Name',
    },
    {
      label: 'TOTAL PENDING',
      kind: 'num',
      tone: 'orangebg',
      total: '745',
    },
    {
      label: 'DISTRICT PENDING',
      kind: 'num',
      tone: 'orangebg',
      total: '453',
    },
    {
      label: 'STATE PENDING',
      kind: 'num',
      tone: 'redbg',
      total: '292',
    },
    {
      label: 'PENDING < 15 DAYS',
      kind: 'num',
      tone: 'greenbg',
      total: '725',
    },
    {
      label: 'PENDING 15-30 DAYS',
      kind: 'num',
      tone: 'orangebg',
      total: '9',
    },
    {
      label: 'PENDING > 30 DAYS',
      kind: 'num',
      tone: 'redbg',
      total: '11',
    },
    {
      label: 'AVG PENDING DAYS',
      kind: 'num',
      total: '4.5',
    },
  ],
  'Recognition Order - DPS [applications]': [
    {
      label: 'APPLICATION ID',
      kind: 'text',
      total: 'App ID',
    },
    {
      label: 'BLOCK',
      kind: 'text',
      total: 'Block Name',
    },
    {
      label: 'UDISE CODE',
      kind: 'text',
      total: 'UDISE',
    },
    {
      label: 'SCHOOL TYPE',
      kind: 'text',
      total: 'Type',
    },
    {
      label: 'PENDING WITH',
      kind: 'text',
      tone: 'blue',
      total: 'Pending With',
    },
    {
      label: 'LEVEL',
      kind: 'text',
      tone: 'blue',
      total: 'Level',
    },
    {
      label: 'STATUS',
      kind: 'text',
      total: 'Status',
    },
    {
      label: 'PENDING DAYS',
      kind: 'num',
      total: 'Days',
    },
  ],
};

const viewOptions = [
  { label: 'Cards (default)', value: 'cards' },
  { label: 'Compact list', value: 'compact' },
  { label: 'KPI grid', value: 'kpi' },
  { label: 'Table view', value: 'table' },
  { label: 'Accordion', value: 'accordion' },
  { label: 'Status board', value: 'status' },
  { label: 'Heatmap', value: 'heatmap' },
];
// Palli Paarvai KPI row label -> its matching tab in PalliPaarvaiDetail
// (view_id 1-6, same order as PalliPaarvaiDetail's own PP_TABS). Palli
// Paarvai renders its own 6-tab panel instead of the generic drill table, so
// clicking a KPI row has to tell it which tab to land on directly.
const PALLI_PAARVAI_KPI_TO_VIEW: Record<string, number> = {
  '% of Class Observation': 1,
  '% of Schools Not Observed (term)': 2,
  '% of Officials Not Observed (month)': 3,
  '% of Schools Observed 3+ Times': 4,
  'Low performers (observation < 75%)': 5,
  'Top performers (observation ≥ 75%)': 6,
};
const ROWS_PER_PAGE = [10, 20, 50];
const assessmentScopes = ['All', 'Q', 'H', 'A'];

const ragOf = (dot?: Dot): Rag =>
  dot === 'red'
    ? 'critical'
    : dot === 'orange'
      ? 'watch'
      : dot === 'green'
        ? 'good'
        : 'none';

const ragMeta: Record<Rag, { label: string; color: string; icon: string }> = {
  critical: {
    label: 'Critical',
    color: '#ef4444',
    icon: 'pi pi-exclamation-triangle',
  },
  watch: { label: 'Watch', color: '#f59e0b', icon: 'pi pi-info-circle' },
  good: { label: 'Good', color: '#16a34a', icon: 'pi pi-check-circle' },
  none: { label: 'No RAG', color: '#64748b', icon: 'pi pi-circle' },
};

/** Normalise a district / block name for case-insensitive matching. */
const normName = (v?: string | null) => (v ?? '').trim().toUpperCase();

const parseNum = (s?: string) =>
  s ? Number(s.replace(/[^0-9.]/g, '')) || 0 : 0;
const shortOf = (c: ReviewCard) => c.short ?? c.title;

/** True when a KPI row's displayed value is 0 (both sides, for split rows). */
const rowIsZero = (row: KpiRow): boolean =>
  row.split
    ? parseNum(row.split.good) === 0 && parseNum(row.split.bad) === 0
    : parseNum(row.value) === 0;

/** True when EVERY row on a card is zero — i.e. the card has no data for the
 * current scope/filters, so its KPI list should show "No data available"
 * instead of a wall of zeros. */
const cardHasNoData = (card: ReviewCard): boolean =>
  card.rows.length > 0 && card.rows.every(rowIsZero);
const fmtIN = (n: number) => n.toLocaleString('en-IN');
const seed = (s: string) =>
  [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 9973, 7);

// ───────────────────────────────────────────────────────────────
// Dummy value generators (deterministic). Swap for API data later.
// ───────────────────────────────────────────────────────────────

/** Core dummy value for one cell. `s` is 0-99, `divisor` scales totals down. */
const cellFromSeed = (
  col: DCol,
  s: number,
  divisor: number
): number | string => {
  if (col.kind === 'text') return col.text ?? '';
  if (col.kind === 'num') {
    const base = parseNum(col.total) / divisor;
    if (base === 0) return 0;
    return Math.max(0, Math.round(base * (0.5 + (s / 100) * 1.3)));
  }
  if (col.kind === 'ratio') {
    const t = parseNum(col.total);
    return +Math.max(0, t - 6 + (s % 15) + (s % 10) / 10).toFixed(2);
  }
  if (col.kind === 'posneg') {
    const neg = col.total.trim().startsWith('-');
    const mag = Math.max(
      0.2,
      parseNum(col.total) - 2 + (s % 5) + (s % 10) / 10
    );
    return neg ? -mag : mag;
  }
  // pct
  const t = parseNum(col.total);
  const lo = Math.max(0, t - 12);
  const hi = Math.min(100, t + 10);
  const span = Math.max(5, Math.round(hi - lo));
  return +(lo + (s % span) + (s % 10) / 10).toFixed(2);
};

/** District level: district index i, column index j (in the full schema). */
const detailCell = (col: DCol, i: number, j: number) =>
  cellFromSeed(col, (i * 31 + j * 17 + 7) % 100, DISTRICTS.length);

/** Block level: assume ~4 blocks per district. */
const blockCell = (col: DCol, bi: number, j: number, district: string) =>
  cellFromSeed(col, seed(`${district}${bi}${j}`) % 100, DISTRICTS.length * 4);

/** School level (generic columns): small per-school numbers. */
const schoolVal = (col: DCol, sd: number, j: number): number | string => {
  const s = (sd + j * 13) % 100;
  if (col.kind === 'num') {
    const t = parseNum(col.total);
    if (t === 0) return 0;
    const mag = t > 1_000_000 ? 1000 : t > 100_000 ? 60 : t > 10_000 ? 12 : 3;
    return Math.round((s / 100) * mag);
  }
  return cellFromSeed(col, s, 1);
};

const fmtDetail = (col: DCol, v: number | string): string => {
  if (col.kind === 'text') return String(v);
  const n = v as number;
  if (col.kind === 'pct') return `${n.toFixed(2)}%`;
  if (col.kind === 'posneg') return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  if (col.kind === 'ratio') return n.toFixed(2);
  return n.toLocaleString('en-IN'); // 0 shows "0", not "—"
};

const detailCellClass = (col: DCol, v: number | string): string => {
  if (col.kind === 'posneg')
    return (v as number) >= 0 ? 'dc-greenbg' : 'dc-redbg';
  if (col.tone === 'pctHi') {
    const n = v as number;
    return n >= 80 ? 'dc-greenbg' : n >= 60 ? 'dc-orangebg' : 'dc-redbg';
  }
  if (col.tone === 'pctLo') {
    const n = v as number;
    return n <= 3 ? 'dc-greenbg' : n <= 4.5 ? 'dc-orangebg' : 'dc-redbg';
  }
  return col.tone && col.tone !== 'none' ? `dc-${col.tone}` : '';
};

/** Grand-total for a list of values: counts are summed, rates are averaged. */
// const aggregate = (col: DCol, vals: (number | string)[]): string => {
//   if (col.kind === 'text' || !vals.length) return '—';
//   const nums = vals as number[];
//   const sum = nums.reduce((a, b) => a + b, 0);
//   return fmtDetail(col, col.kind === 'num' ? sum : sum / nums.length);
// };
const aggregate = (col: DCol, vals: (number | string)[]): string => {
  if (col.kind === 'text' || !vals.length) return '—';
  const nums = vals as number[];
  console.log(col, 'col');
  console.log(vals, 'vals');
  // if(col.apiKey='transition_not_meeting_100_pct') {
  //   var temp_sum_n =
  //   var temp_sum_ =
  //   return fmtDetail(col, col.kind === 'num' ? sum : sum / nums.length);
  // }
  const sum = nums.reduce((a, b) => a + b, 0);
  return fmtDetail(col, col.kind === 'num' ? sum : sum / nums.length);
};

const groupAggregate = (col: DCol, vals: Record<string, unknown>[]): string => {
  let temp_count = 0;
  let temp_total = 0;

  vals.map((v) => {
    if (col.apiKey == 'transition_not_meeting_100_pct') {
      temp_count += Number(v.transition_not_meeting_100_pct_count);
      temp_total += Number(v.transition_not_meeting_100_pct_total_count);
    } else if (col.apiKey == 'student_attendance_pct') {
      temp_count += Number(v.student_attendance_count);
      temp_total += Number(v.student_attendance_total);
    } else if (col.apiKey == 'teacher_attendance_pct') {
      temp_count += Number(v.teacher_attendance_count);
      temp_total += Number(v.teacher_attendance_total);
    } else if (col.apiKey == 'school_non_mark_pct') {
      temp_count += Number(v.school_non_mark_count);
      temp_total += Number(v.school_non_mark_total);
    } else if (col.apiKey == 'pct_of_enrolment') {
      temp_count += Number(v.thiran_students_identified);
      temp_total += Number(v.pct_of_enrolment_total);
    } else if (col.apiKey == 'blo_attainment_pct') {
      temp_count += Number(v.blo_attainment_count);
      temp_total += Number(v.thiran_students_identified);
    } else if (col.apiKey == 'baseline_update_pct') {
      temp_count += Number(v.baseline_update_count);
      temp_total += Number(v.thiran_students_identified);
    } else if (col.apiKey == 'payment_pending_pct') {
      temp_count += Number(v.payment_pending);
      temp_total += Number(v.eligible_students);
    } else if (col.apiKey == 'payment_failed_pct') {
      temp_count += Number(v.payment_failed);
      temp_total += Number(v.eligible_students);
    } else if (col.apiKey == 'payment_success_pct') {
      temp_count += Number(v.payment_success);
      temp_total += Number(v.eligible_students);
    } else if (col.apiKey == 'closure_rate') {
      temp_count += Number(v.resolved_cnt);
      temp_total += Number(v.res_count);
    }
  });

  console.log(fmtDetail(col, (temp_count / temp_total) * 100));
  // for (v in vals) {
  //   console.log
  // }
  return temp_count == 0 || temp_total == 0
    ? '0%'
    : fmtDetail(col, (temp_count / temp_total) * 100);
};
// ── Blocks ──
const BLOCK_NAMES: Record<string, string[]> = {
  Perambalur: ['Alathur', 'Veppanthattai', 'Perambalur', 'Veppur'],
  Nagapattinam: ['Kilvelur', 'Mayiladuthurai', 'Nagapattinam', 'Thirukkuvalai'],
  'The Nilgiris': ['Coonoor', 'Kotagiri', 'Udhagamandalam', 'Kundah'],
  Ariyalur: ['Andimadam', 'Ariyalur', 'Jayamkondam', 'Udayarpalayam'],
  Mayiladuthurai: ['Kumbakonam', 'Mayiladuthurai', 'Payyur', 'Sirkazhi'],
  Theni: ['Andipatti', 'Cumbum', 'Periakulam', 'Theni'],
  Karur: ['Aravakurichi', 'Karur', 'Kulithalai', 'Manmangalam'],
};
const makeBlocks = (district: string): string[] =>
  BLOCK_NAMES[district] ?? [1, 2, 3, 4].map((n) => `${district} Block ${n}`);

// ── Schools ──
const SCHOOL_CATEGORIES = [
  'Primary School',
  'Middle School',
  'High School',
  'Higher Secondary School',
];

const makeSchools = (district: string, block: string, count = 25) =>
  Array.from({ length: count }, (_, i) => {
    const s = seed(`${district}${block}${i}`);
    const total = 20 + (s % 1400);
    const boys = Math.round(total * (0.4 + (s % 20) / 100));
    return {
      seedN: s,
      sno: i + 1,
      district,
      block,
      udise: `3316${String(400000 + s).slice(0, 6)}`,
      name: `GOVT ${SCHOOL_CATEGORIES[s % 4].toUpperCase()} ${block.toUpperCase()} ${i + 1}`,
      category: SCHOOL_CATEGORIES[s % 4],
      total,
      boys,
      girls: total - boys, // boys + girls always equals total
      teachers: Math.max(1, Math.round(total / (25 + (s % 15)))),
      notMoved: s % 60,
      enrollChange: +(((s % 300) - 150) / 10).toFixed(2),
      ptr: +(8 + (s % 300) / 10).toFixed(2),
      dropoutRate: +((s % 70) / 10).toFixed(2),
      dropoutStudents: s % 30,
    };
  });

type SchoolRow = ReturnType<typeof makeSchools>[number];

// ── Recognition Order data ──
interface RecognitionOrderRow {
  seedN: number;
  district: string;
  totalPending: number;
  districtPending: number;
  statePending: number;
  pending15: number;
  pending1530: number;
  pendingOver30: number;
  avgPendingDays: number;
}

const makeRecognitionOrderRows = (
  district: string,
  count = 38
): RecognitionOrderRow[] =>
  Array.from({ length: count }, (_, i) => {
    const s = seed(`recognition-${district}${i}`);
    const totalPending = 20 + (s % 100);
    const districtPending = Math.floor(totalPending * (0.4 + (s % 30) / 100));
    const statePending = totalPending - districtPending;
    const pending15 = Math.floor(totalPending * (0.7 + (s % 20) / 100));
    const pending1530 = Math.max(0, Math.floor(totalPending * 0.15));
    const pendingOver30 = totalPending - pending15 - pending1530;
    const avgPendingDays = Math.round(5 + (s % 25));

    return {
      seedN: s,
      district,
      totalPending,
      districtPending,
      statePending,
      pending15,
      pending1530,
      pendingOver30,
      avgPendingDays,
    };
  });

type RecognitionOrderRowType = ReturnType<
  typeof makeRecognitionOrderRows
>[number];

// ── Recognition Order Applications ──
interface RecognitionOrderApplication {
  seedN: number;
  applicationId: string;
  district: string;
  block: string;
  udiseCode: string;
  schoolType: string;
  pendingWith: 'District' | 'State';
  status: 'pending' | 'verified' | 'verification1' | 'approved';
  pendingDays: number;
}

const BLOCKS_MAP: Record<string, string[]> = {
  Coimbatore: [
    'Coimbatore',
    'Nilgiris',
    'Pollachis south',
    'Sarkar Samakkulam',
    'Karamadai',
  ],
  Chennai: ['Chennai', 'Avadi', 'Chengalpattu', 'Kanchipuram', 'Tiruvallur'],
  Madurai: ['Madurai', 'Melur', 'Vadipatti', 'Usilampatti', 'Tirumangalam'],
};

const SCHOOL_TYPES = [
  'Metric',
  'Matric',
  'Nursery & Primary',
  'CBSE',
  'Others',
];

const makeRecognitionOrderApplications = (
  district: string,
  pendingFilter: 'total' | 'district' | 'state' | 'pending15' | null,
  count = 36
): RecognitionOrderApplication[] => {
  const blocks = BLOCKS_MAP[district] ?? [
    `${district} Block 1`,
    `${district} Block 2`,
    `${district} Block 3`,
  ];

  return Array.from({ length: count }, (_, i) => {
    const s = seed(`app-${district}${i}`);
    const blockIdx = s % blocks.length;
    const block = blocks[blockIdx];
    const applicationId = `${String(300001 + s).slice(0, 8)}`;
    const udiseCode = `33${String(1270000 + s).slice(0, 6)}`;
    const schoolType = SCHOOL_TYPES[s % SCHOOL_TYPES.length];

    // Determine pending status based on filter
    let pendingWith: 'District' | 'State' = 'District';
    let status: 'pending' | 'verified' | 'verification1' | 'approved' =
      'pending';
    let pendingDays = 5 + (s % 60);

    if (pendingFilter === 'district') {
      pendingWith = 'District';
      pendingDays = 3 + (s % 40);
    } else if (pendingFilter === 'state') {
      pendingWith = 'State';
      pendingDays = 10 + (s % 80);
      status = (s % 3 === 0 ? 'verification1' : 'pending') as any;
    } else if (pendingFilter === 'pending15') {
      pendingDays = 1 + (s % 14);
      pendingWith = s % 2 === 0 ? 'District' : 'State';
    } else if (pendingFilter === 'total') {
      pendingWith = s % 3 === 0 ? 'State' : 'District';
      pendingDays = 1 + (s % 60);
    }

    return {
      seedN: s,
      applicationId,
      district,
      block,
      udiseCode,
      schoolType,
      pendingWith,
      status,
      pendingDays,
    };
  });
};

type RecognitionOrderApplicationType = ReturnType<
  typeof makeRecognitionOrderApplications
>[number];

interface SchoolKpiCol {
  key: string;
  head: React.ReactNode;
  val?: (s: SchoolRow) => number; // numeric value → enables column sorting
  cell: (s: SchoolRow) => React.ReactNode;
  foot: (r: SchoolRow[]) => React.ReactNode;
}

const sumOf = (r: SchoolRow[], f: (s: SchoolRow) => number) =>
  r.reduce((a, s) => a + f(s), 0);
const avgOf = (r: SchoolRow[], f: (s: SchoolRow) => number) =>
  r.length ? sumOf(r, f) / r.length : 0;

// Rich school-level columns used by State Summary
const SCHOOL_COLS: SchoolKpiCol[] = [
  {
    key: 'Total students',
    head: (
      <>
        TOTAL
        <br />
        STUDENTS
      </>
    ),
    val: (s) => s.total,
    cell: (s) => fmtIN(s.total),
    foot: (r) => fmtIN(sumOf(r, (s) => s.total)),
  },
  {
    key: 'Boys',
    head: 'Boys',
    val: (s) => s.boys,
    cell: (s) => fmtIN(s.boys),
    foot: (r) => fmtIN(sumOf(r, (s) => s.boys)),
  },
  {
    key: 'Girls',
    head: 'Girls',
    val: (s) => s.girls,
    cell: (s) => fmtIN(s.girls),
    foot: (r) => fmtIN(sumOf(r, (s) => s.girls)),
  },
  {
    key: 'Teachers',
    head: 'Teachers',
    val: (s) => s.teachers,
    cell: (s) => fmtIN(s.teachers),
    foot: (r) => fmtIN(sumOf(r, (s) => s.teachers)),
  },
  {
    key: 'Students not moved to next class',
    head: 'Students not moved to next class',
    val: (s) => s.notMoved,
    cell: (s) => (
      <span
        className={`rd-pill ${s.notMoved === 0 ? 'dc-greenbg' : 'dc-orangebg'}`}
      >
        {s.notMoved}
      </span>
    ),
    foot: (r) => fmtIN(sumOf(r, (s) => s.notMoved)),
  },
  {
    key: 'Enrollment change',
    head: 'Enrollment change',
    val: (s) => s.enrollChange,
    cell: (s) => (
      <span
        className={`rd-pill ${
          s.enrollChange >= 0
            ? 'dc-greenbg'
            : s.enrollChange >= -3
              ? 'dc-orangebg'
              : 'dc-redbg'
        }`}
      >
        {s.enrollChange > 0 ? '+' : ''}
        {s.enrollChange}%
      </span>
    ),
    foot: (r) => `${avgOf(r, (s) => s.enrollChange).toFixed(2)}%`,
  },
  {
    key: 'PTR',
    head: 'PTR',
    val: (s) => s.ptr,
    cell: (s) => (
      <span
        className={`rd-pill ${
          s.ptr <= 30 ? 'dc-greenbg' : s.ptr <= 40 ? 'dc-orangebg' : 'dc-redbg'
        }`}
      >
        {s.ptr}
      </span>
    ),
    foot: (r) => avgOf(r, (s) => s.ptr).toFixed(2),
  },
  {
    key: 'Dropout rate',
    head: (
      <>
        Dropout
        <br />
        rate
      </>
    ),
    val: (s) => s.dropoutRate,
    cell: (s) => (
      <span
        className={`rd-pill ${
          s.dropoutRate <= 2
            ? 'dc-greenbg'
            : s.dropoutRate <= 5
              ? 'dc-orangebg'
              : 'dc-redbg'
        }`}
      >
        {s.dropoutRate}%
      </span>
    ),
    foot: (r) => `${avgOf(r, (s) => s.dropoutRate).toFixed(2)}%`,
  },
  {
    key: 'Dropout students',
    head: (
      <>
        Dropout
        <br />
        students
      </>
    ),
    val: (s) => s.dropoutStudents,
    cell: (s) => s.dropoutStudents,
    foot: (r) => fmtIN(sumOf(r, (s) => s.dropoutStudents)),
  },
];

/** School-level KPI columns for ANY component (State Summary has its own). */
const buildSchoolCols = (title: string): SchoolKpiCol[] => {
  if (title === 'State Summary') return SCHOOL_COLS;
  return (detailSchemas[title] ?? []).map((col, j) => ({
    key: col.label,
    head: col.label,
    val:
      col.kind === 'text'
        ? undefined
        : (s: SchoolRow) => Number(schoolVal(col, s.seedN, j)) || 0,
    cell: (s: SchoolRow) => {
      const v = schoolVal(col, s.seedN, j);
      const cls = detailCellClass(col, v);
      return (
        <span className={cls ? `rd-pill ${cls}` : ''}>{fmtDetail(col, v)}</span>
      );
    },
    foot: (r: SchoolRow[]) =>
      aggregate(
        col,
        r.map((s) => schoolVal(col, s.seedN, j))
      ),
  }));
};

const schoolKpiOptionsFor = (title: string): KpiOption[] =>
  buildSchoolCols(title).map((c) => ({ label: c.key, value: c.key }));

// ───────────────────────────────────────────────────────────────
// Per-section SCHOOL-level columns. Each card drills to a school table whose
// columns match that section (not a shared set). For the live cards
// (Enrollment / Attendance / Infrastructure) each column reads the API item by
// `apiKey`; Enrollment's keys are confirmed from the real response, Attendance
// / Infrastructure use the expected snake_case names and fall back to a
// deterministic placeholder per cell when the field is absent, so the columns
// still render. Non-API cards are fully placeholder-filled.
// ───────────────────────────────────────────────────────────────
type SMKind = 'num' | 'pct' | 'signedPct' | 'ratio' | 'text' | 'yn';
interface SchoolMetric {
  label: string;
  /** Field name on the API school item (live cards). */
  apiKey: string;
  kind: SMKind;
  /** Threshold colouring for percentage columns. */
  tone?: 'pctHi' | 'pctLo';
}

// Enrollment + State Summary share the same per-school profile.
const ENROLLMENT_SCHOOL_METRICS: SchoolMetric[] = [
  { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
  { label: 'Boys', apiKey: 'boys', kind: 'num' },
  { label: 'Girls', apiKey: 'girls', kind: 'num' },
  { label: 'Teachers', apiKey: 'teachers', kind: 'num' },
  {
    label: 'Students not moved to next class',
    apiKey: 'students_not_moved',
    kind: 'num',
  },
  {
    label: 'Enrollment change %',
    apiKey: 'enrollment_change_pct',
    kind: 'signedPct',
  },
  { label: 'PTR', apiKey: 'ptr', kind: 'ratio' },
  { label: 'Dropout rate %', apiKey: 'dropout_rate_pct', kind: 'pct' },
  { label: 'Dropout students', apiKey: 'dropout_students', kind: 'num' },
];

const SCHOOL_METRICS: Record<string, SchoolMetric[]> = {
  'State Summary': ENROLLMENT_SCHOOL_METRICS,
  Enrollment: ENROLLMENT_SCHOOL_METRICS,
  Attendance: [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    {
      label: 'Student present %',
      apiKey: 'student_present_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
    { label: 'Boys absent', apiKey: 'boys_absent', kind: 'num' },
    { label: 'Girls absent', apiKey: 'girls_absent', kind: 'num' },
    {
      label: 'Total students absent',
      apiKey: 'total_students_absent',
      kind: 'num',
    },
    { label: 'Total Teachers', apiKey: 'total_teachers', kind: 'num' },
    {
      label: 'Teacher present %',
      apiKey: 'teacher_present_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
    { label: 'Teacher present', apiKey: 'teacher_present', kind: 'num' },
    { label: 'Teacher absent', apiKey: 'teacher_absent', kind: 'num' },
    {
      label: 'Teacher long absent(30+ days)',
      apiKey: 'teacher_absent_30_plus',
      kind: 'num',
    },
    { label: 'Marked status', apiKey: 'marked_status', kind: 'text' },
    { label: 'Potential dropout', apiKey: 'potential_dropout', kind: 'num' },
  ],
  Infrastructure: [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    {
      label: 'Classroom Available',
      apiKey: 'classroom_available',
      kind: 'num',
    },
    {
      label: 'Boys Toilet Available',
      apiKey: 'boys_toilet_available',
      kind: 'num',
    },
    {
      label: 'Girls Toilet Available',
      apiKey: 'girls_toilet_available',
      kind: 'num',
    },
    { label: 'CWSN Toilet', apiKey: 'cwsn_toilet', kind: 'num' },
    { label: 'Lab Available', apiKey: 'lab_available', kind: 'num' },
    { label: 'Drinking Water', apiKey: 'drinking_water', kind: 'num' },
    { label: 'Compound Wall', apiKey: 'compound_wall', kind: 'yn' },
    {
      label: 'Building to be Demolished',
      apiKey: 'building_to_be_demolished',
      kind: 'num',
    },
    { label: 'Kitchen Shed', apiKey: 'kitchen_shed', kind: 'yn' },
  ],
  'Academic Performance': [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    {
      label: 'Pass Percentage %',
      apiKey: 'pass_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Average Mark',
      apiKey: 'average_mark',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Year-on-Year Improvement %',
      apiKey: 'yoy_improvement',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Exam-to-Exam Improvement (PP)',
      apiKey: 'exam_to_exam_pp',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Assessment Coverage %',
      apiKey: 'assessment_coverage',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Mark Entry Completion %',
      apiKey: 'mark_entry_completion',
      kind: 'pct',
      tone: 'pctHi',
    },
  ],
  THIRAN: [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    {
      label: 'THIRAN students identified',
      apiKey: 'thiran_students_identified',
      kind: 'num',
    },
    { label: 'Schools covered', apiKey: 'schools_covered', kind: 'num' },
    {
      label: '% of enrolment',
      apiKey: 'pct_of_enrolment',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'BLO attainment %',
      apiKey: 'blo_attainment_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Baseline update %',
      apiKey: 'baseline_update_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
  ],
  Scholarship: [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    { label: 'Eligible Students', apiKey: 'eligible_students', kind: 'num' },
    { label: 'Payment Success', apiKey: 'payment_success', kind: 'num' },
    {
      label: 'Payment Success %',
      apiKey: 'payment_success_pct',
      kind: 'pct',
      tone: 'pctHi',
    },
    { label: 'Payment Failed', apiKey: 'payment_failed', kind: 'num' },
    {
      label: 'Payment Failed %',
      apiKey: 'payment_failed_pct',
      kind: 'pct',
      tone: 'pctLo',
    },
    { label: 'Payment Pending', apiKey: 'payment_pending', kind: 'num' },
    {
      label: 'Payment Pending %',
      apiKey: 'payment_pending_pct',
      kind: 'pct',
      tone: 'pctLo',
    },
    { label: 'NPCI Inactive', apiKey: 'npci_inactive', kind: 'num' },
    { label: 'Aadhaar Not Updated', apiKey: 'aadhar_not_updated', kind: 'num' },
  ],
  SMC: [
    { label: 'Total Students', apiKey: 'total_students', kind: 'num' },
    {
      label: 'SMC resolutions raised',
      apiKey: 'res_count',
      kind: 'num',
    },
    { label: 'Resolutions closed', apiKey: 'resolved_cnt', kind: 'num' },
    {
      label: 'Pending resolutions',
      apiKey: 'pending_cnt',
      kind: 'num',
    },
    {
      label: 'Closure rate %',
      apiKey: 'closure_rate',
      kind: 'pct',
      tone: 'pctHi',
    },
    {
      label: 'Emergency resolutions not closed',
      apiKey: 'emergency_cnt',
      kind: 'num',
    },
  ],
};

/** A school-table row: identity fields + any metric values, keyed by apiKey. */
type SchoolRec = {
  sno: number;
  district: string;
  block: string;
  udise: string;
  name: string;
  category: string;
  _seed: number;
  [key: string]: number | string;
};

const toNum = (v: unknown): number => {
  if (typeof v === 'number') return v;
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const fmtSchoolMetric = (col: SchoolMetric, v: number | string): string => {
  if (col.kind === 'text') return String(v ?? '');
  if (col.kind === 'yn') {
    const s = String(v).trim().toLowerCase();
    return s === '1' || s === 'yes' || s === 'true' || s === 'y' ? 'Yes' : 'No';
  }
  const n = toNum(v);
  if (col.kind === 'pct') return `${n.toFixed(2)}%`;
  if (col.kind === 'signedPct') return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  if (col.kind === 'ratio') return n.toFixed(2);
  return n.toLocaleString('en-IN');
};

const schoolMetricClass = (col: SchoolMetric, v: number | string): string => {
  if (col.kind === 'signedPct') {
    const n = toNum(v);
    return n >= 0 ? 'dc-greenbg' : n >= -3 ? 'dc-orangebg' : 'dc-redbg';
  }
  if (col.tone === 'pctHi') {
    const n = toNum(v);
    return n >= 80 ? 'dc-greenbg' : n >= 60 ? 'dc-orangebg' : 'dc-redbg';
  }
  if (col.tone === 'pctLo') {
    const n = toNum(v);
    return n <= 3 ? 'dc-greenbg' : n <= 4.5 ? 'dc-orangebg' : 'dc-redbg';
  }
  return '';
};

/** Footer: counts sum, rates average, text / yn have no total. */
const aggSchoolMetric = (
  col: SchoolMetric,
  vals: (number | string)[]
): string => {
  if (col.kind === 'text' || col.kind === 'yn' || !vals.length) return '';
  const nums = vals.map(toNum);
  const sum = nums.reduce((a, b) => a + b, 0);
  const avg =
    col.kind === 'pct' || col.kind === 'signedPct' || col.kind === 'ratio';
  return fmtSchoolMetric(col, avg ? sum / nums.length : sum);
};

/** Deterministic placeholder value for one school metric cell. */
const dummySchoolMetricVal = (
  col: SchoolMetric,
  seedN: number,
  j: number
): number | string => {
  const s = (seedN + j * 13) % 100;
  if (col.kind === 'text') return s % 5 === 0 ? 'Not marked' : 'Marked';
  if (col.kind === 'yn') return s % 4 === 0 ? 0 : 1;
  if (col.kind === 'signedPct') return +(((s % 200) - 100) / 10).toFixed(2);
  if (col.kind === 'pct') return +(55 + (s % 45) + (s % 10) / 10).toFixed(2);
  if (col.kind === 'ratio') return +(8 + (s % 300) / 10).toFixed(2);
  if (col.label === 'Total Students') return 20 + ((s * 37) % 1400);
  return s % 60;
};

// One place to switch between dummy data and the API
const USE_API = false;

const fetchSchoolKpiOptions = async (title: string): Promise<KpiOption[]> => {
  if (!USE_API) return schoolKpiOptionsFor(title);
  const res = await fetch(
    `/api/review/school-kpis?component=${encodeURIComponent(title)}`
  ); // your real endpoint
  const data: { key: string; name: string }[] = await res.json();
  return data.map((d) => ({ label: d.name, value: d.key }));
};

/**
 * Overlay a card row with its live reviewdashboard value (matched by apiKey).
 * Counts are grouped Indian-style; `_pct` keys get a "%"; split rows show the
 * value on its semantic side and the complement on the other. Rows without an
 * apiKey, or whose key isn't in the response, are returned unchanged.
 */
const pctDot = (pct: number): Dot =>
  pct >= 75 && pct < 100
    ? 'green'
    : pct >= 50 && pct <= 74.5
      ? 'orange'
      : pct <= 50
        ? 'red'
        : 'gray';
const PCT_DOT_CARDS = new Set([
  'Enrollment',
  'Attendance',
  'SMC',
  'THIRAN',
  'Scholarship',
  'Palli Paarvai',
]);

const isPctKey = (key?: string) =>
  !!key &&
  (key.endsWith('_pct') ||
    key === 'closure_rate' ||
    key === 'pct_class_observation' ||
    key === 'pct_schools_not_observed' ||
    key === 'pct_officials_not_observed' ||
    key === 'pct_observed_3plus' ||
    key === 'pct_of_enrolment' ||
    key === 'Average Mark' ||
    key === 'YoY Improvement' ||
    key === 'Exam to Exam Pass Point' ||
    key === 'Assessment Coverage' ||
    key === 'Pass Percentage %' ||
    key === 'Mark Entry Completion');

const isPctRow = (row: KpiRow) => !!row.split || isPctKey(row.apiKey);
const applyLiveRow = (
  row: KpiRow,
  values: Record<string, string>,
  colorByPct = false
): KpiRow => {
  if (!row.apiKey) return row;
  const raw = values[row.apiKey];
  if (raw == null || raw === '') return row;
  const num = Number(raw);
  const isPct = isPctKey(row.apiKey);

  if (row.split) {
    const main = Number.isNaN(num) ? 0 : num;
    const other = +(100 - main).toFixed(2);
    const good = row.apiValueIsBad ? other : main;
    const bad = row.apiValueIsBad ? main : other;
    return {
      ...row,
      split: { good: `${good}%`, bad: `${bad}%` },
      dot: colorByPct ? pctDot(main) : row.dot, // colour by the API value
    };
  }

  const value = isPct
    ? `${raw}%`
    : Number.isNaN(num)
      ? String(raw)
      : num.toLocaleString('en-IN');

  return {
    ...row,
    value,
    ...(colorByPct && isPct && !Number.isNaN(num) ? { dot: pctDot(num) } : {}),
  };
};
// const applyLiveRow = (row: KpiRow, values: Record<string, string>): KpiRow => {
//   if (!row.apiKey) return row;
//   const raw = values[row.apiKey];
//   if (raw == null || raw === '') return row;
//   const num = Number(raw);
//   // Every other percentage KPI key ends in `_pct` by convention — SMC's
//   // closure rate (res_count/resolved_cnt/pending_cnt/emergency_cnt/
//   // closure_rate) is the one exception from the real backend, so it's
//   // special-cased here rather than renaming the lookup key.
//   const isPct =
//     row.apiKey.endsWith('_pct') ||
//     row.apiKey === 'closure_rate' ||
//     row.apiKey === 'pct_of_enrolment';
//   if (row.split) {
//     const main = Number.isNaN(num) ? 0 : num;
//     const other = +(100 - main).toFixed(2);
//     const good = row.apiValueIsBad ? other : main;
//     const bad = row.apiValueIsBad ? main : other;
//     return {
//       ...row,
//       split: { good: `${good}%`, bad: `${bad}%` },
//       dot: pctDot(main), // colour by the "good" side
//     };
//   }
//   const value = isPct
//     ? `${raw}%`
//     : Number.isNaN(num)
//       ? String(raw)
//       : num.toLocaleString('en-IN');
//   return { ...row, value };
// };

/** Render a card KPI value (split → green/red, else tone-coloured). */
const Val: React.FC<{ row: KpiRow }> = ({ row }) => {
  if (row.split) {
    return (
      <>
        <span className="rv-good">{row.split.good}</span>
        <span className="rv-sep">/</span>
        <span className="rv-bad">{row.split.bad}</span>
      </>
    );
  }
  return (
    <span className={row.tone ? `rv-${row.tone}` : ''}>{row.value ?? '—'}</span>
  );
};

// Row shape used by the District / Block DataTable: { name, k0, k1, ... }
type DRow = { name: string; [k: string]: number | string };

interface ReviewDashboardProps {
  selectedProgramme?: string | null;
  selectedManagement?: string[];
  selectedCategory?: string[];
  selectedSchoolType?: string[];
  /**
   * Notify the parent header whenever the grid drill changes, so the top
   * District / Block dropdowns stay in sync. `null` values mean that level is
   * cleared (e.g. back to all districts).
   */
  onGeoSync?: (geo: {
    district: string | null;
    block: string | null;
    school?: string | null;
  }) => void;
  /**
   * Sets district + block + school all at once (Dashboard.tsx's
   * `handleGeoSelect`) — unlike `onGeoSync` above, this one actually applies
   * `school` instead of dropping it. Forwarded straight through to
   * PalliPaarvaiDetail's own `onGeoSelect`, same direct wiring Caution.tsx /
   * InfrastructureCaution.tsx already use for their row-click → header sync.
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
  /** Header-selected district / block, so the grid can mirror header changes. */
  headerDistrict?: string | null;
  headerBlock?: string | null;
  /** Resolved master id of `headerDistrict`, for district-scoped APIs (academickpis). */
  headerDistrictId?: number | null;
  /** Resolved master id of `headerBlock`, for block-scoped card APIs (reviewdashboard1). */
  headerBlockId?: number | null;

  /** Header-selected school name. */
  headerSchool?: string | null;
  /** Header-selected school UDISE code. */
  headerSchoolUdise?: string | null;
  /** Callback to change/clear selected school. */
  onSchoolChange?: (school: string | null) => void;
  /** Callback when a school row is selected in the review drill. */
  onSchoolSelect?: (school: {
    name: string;
    udise?: string;
    cardTitle: string;
  }) => void;
  /** Controlled active card detail title. */
  activeDetail?: string | null;
  /** Callback when the active card detail changes or is closed. */
  onDetailChange?: (detail: string | null) => void;
  setCardInPalliPaarvai?: (card: string) => void;
  /** Reports PalliPaarvaiDetail's active tab (1-6) up to Dashboard.tsx, so
   * the header's Category filter can be hidden specifically on the
   * % Class Observation (1) / % Officials Not Observed (3) tabs. */
  setPalliPaarvaiViewId?: (viewId: number | null) => void;
  /** Forwarded straight through to PalliPaarvaiDetail's own onScopeOptions —
   * narrows the header's Block/School dropdowns to what's in its grid on
   * views 2 (school level) / 4, same as Caution.tsx / InfrastructureCaution.
   * Reuses Dashboard.tsx's shared cautionScope state. */
  onScopeOptions?: (
    opts: {
      blocks: { label: string; value: string }[];
      schools: { label: string; value: string }[];
    } | null
  ) => void;
  /** Current academic year id (e.g. 37 → 2026-27) — forwarded to the Palli
   * Paarvai detail panel's pp_details calls. */
  currentAcdYr?: number | string | null;
  /** District/block-level users are pinned to their own district/block (the
   * header dropdowns are frozen for them — see Dashboard.tsx). The drill-down
   * breadcrumb here must honor the same restriction: it must never offer a
   * "go up" link that would clear their fixed district/block and show scope
   * they aren't permitted to see. */
  districtLocked?: boolean;
  blockLocked?: boolean;
}

const ReviewDashboard: React.FC<ReviewDashboardProps> = ({
  selectedProgramme = null,
  selectedManagement = [],
  selectedCategory = [],
  selectedSchoolType = [],
  onGeoSync,
  headerDistrict = null,
  headerBlock = null,
  headerDistrictId = null,
  headerBlockId = null,
  headerSchool = null,
  headerSchoolUdise = null,
  onSchoolChange,
  onSchoolSelect,
  activeDetail,
  onDetailChange,
  setCardInPalliPaarvai,
  setPalliPaarvaiViewId,
  onGeoSelect,
  onScopeOptions,
  currentAcdYr = null,
  districtLocked = false,
  blockLocked = false,
}) => {
  // Header classification filters → comma-separated id lists, PLUS the header
  // District / Block selection (resolved to ids by the parent), for every
  // review* endpoint (manage_id / cate_id / school_type_id / district_id /
  // block_id). Shared by the card-tile calls (reviewdashboard, reviewdashboard1,
  // academickpis) and the grid drills, so selecting a district/block up top
  // scopes ALL of them consistently.
  const reviewFilterArgs = useMemo(() => {
    const csv = (a?: string[]) =>
      a && a.length ? a.map(String).filter(Boolean).join(',') : undefined;
    return {
      ...(headerDistrictId != null ? { district_id: headerDistrictId } : {}),
      ...(headerBlockId != null ? { block_id: headerBlockId } : {}),
      ...(csv(selectedManagement)
        ? { manage_id: csv(selectedManagement) }
        : {}),
      ...(csv(selectedCategory) ? { cate_id: csv(selectedCategory) } : {}),
      ...(csv(selectedSchoolType)
        ? { school_type_id: csv(selectedSchoolType) }
        : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    headerDistrictId,
    headerBlockId,
    selectedManagement?.join(','),
    selectedCategory?.join(','),
    selectedSchoolType?.join(','),
  ]);

  // ── Drill-down open/closed state, declared early so the card-tile queries
  // below can skip themselves while a detail view is open (see note there). ──
  const [internalDetail, setInternalDetail] = useState<string | null>(
    activeDetail || null
  );
  const detail = activeDetail !== undefined ? activeDetail : internalDetail;
  const setDetail = (d: string | null) => {
    setInternalDetail(d);
    onDetailChange?.(d);
  };
  useEffect(() => {
    setCardInPalliPaarvai?.(detail === 'Palli Paarvai' ? 'Palli Paarvai' : '');
    // Leaving the Palli Paarvai card entirely — clear the reported tab too,
    // so a stale viewId can't linger and hide the Category filter elsewhere.
    if (detail !== 'Palli Paarvai') setPalliPaarvaiViewId?.(null);
  }, [detail, setCardInPalliPaarvai, setPalliPaarvaiViewId]);
  // Review Dashboard state-level payload (api/dashboard/reviewdashboard).
  // Fired automatically when this tab mounts (and on filter change); logged to
  // the console so we can decide the UI wiring from the real data shape.
  //
  // Skipped while a card's detail/drill is open: it feeds only the cards grid,
  // which isn't visible then, and leaving it running meant every drill click
  // (which updates reviewFilterArgs via the header district/block sync) kept
  // refetching THIS plus reviewdashboard1 plus academickpis all at once,
  // competing with — and delaying — the one table query the open drill
  // actually needs.
  const {
    data: reviewData,
    isFetching: reviewLoading,
    error: reviewError,
  } = useGetReviewDashboardQuery(reviewFilterArgs, { skip: detail !== null });
  useEffect(() => {
    if (detail !== null || reviewLoading) return;
    if (reviewError) {
      // eslint-disable-next-line no-console
      console.error('[reviewdashboard] error', reviewError);
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[reviewdashboard] response', reviewData);
  }, [detail, reviewData, reviewLoading, reviewError]);

  // State declarations (needed before useMemos that depend on them)
  const [view, setView] = useState('cards');
  const [assessment, setAssessment] = useState('All');
  const [openIdx, setOpenIdx] = useState<number | null>(0); // accordion

  // Flatten the available reviewdashboard sections into one key → value map.
  const reviewKpiValues = useMemo(() => {
    const out: Record<string, string> = {};
    const result = (
      reviewData as { result?: Record<string, unknown> } | undefined
    )?.result;
    (['attendance', 'enrollment', 'infrastructure'] as const).forEach((sec) => {
      const kpis = (result?.[sec] as { kpis?: unknown } | undefined)?.kpis;
      if (Array.isArray(kpis)) {
        kpis.forEach((k) => {
          const kpi = k as { key?: unknown; value?: unknown };
          if (kpi?.key != null) out[String(kpi.key)] = String(kpi.value ?? '');
        });
      }
    });
    return out;
  }, [reviewData]);

  // Scholarship + THIRAN card KPIs (api/dashboard/reviewdashboard1). Split out
  // of reviewdashboard (slow combined payload) into its own call; reuses
  // reviewFilterArgs, which already carries district_id / block_id from the
  // header plus the classification filters. Skipped while a detail/drill is
  // open — see the note on the reviewdashboard query above.
  const {
    data: reviewData1,
    isFetching: reviewData1Loading,
    error: reviewData1Error,
  } = useGetReviewDashboard1Query(reviewFilterArgs, { skip: detail !== null });
  useEffect(() => {
    if (detail !== null || reviewData1Loading) return;
    if (reviewData1Error) {
      // eslint-disable-next-line no-console
      console.error('[reviewdashboard1] error', reviewData1Error);
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[reviewdashboard1] response', reviewData1);
  }, [detail, reviewData1, reviewData1Loading, reviewData1Error]);

  // Flatten reviewdashboard1's scholarship + thiran sections.
  const reviewKpiValues1 = useMemo(() => {
    const out: Record<string, string> = {};
    const result = (
      reviewData1 as { result?: Record<string, unknown> } | undefined
    )?.result;
    (['scholarship', 'thiran', 'smc', 'palli_paarvai'] as const).forEach(
      (sec) => {
        const kpis = (result?.[sec] as { kpis?: unknown } | undefined)?.kpis;
        if (Array.isArray(kpis)) {
          kpis.forEach((k) => {
            const kpi = k as { key?: unknown; value?: unknown };
            if (kpi?.key != null)
              out[String(kpi.key)] = String(kpi.value ?? '');
          });
        }
      }
    );
    return out;
  }, [reviewData1]);

  // Academic Performance card detail (api/dashboard/academickpis). This used
  // to be the `academic` section of reviewdashboard, but that part was slow
  // enough to be dropped from the combined payload — it's now fetched here on
  // its own; reuses reviewFilterArgs, which already carries district_id /
  // block_id from the header plus the classification filters. Skipped while a
  // detail/drill is open — see the note on the reviewdashboard query above.
  const {
    data: academicKpisData,
    isFetching: academicKpisLoading,
    error: academicKpisError,
  } = useGetAcademicKpisQuery(reviewFilterArgs, { skip: detail !== null });
  useEffect(() => {
    if (detail !== null || academicKpisLoading) return;
    if (academicKpisError) {
      // eslint-disable-next-line no-console
      console.error('[academickpis] error', academicKpisError);
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[academickpis] response', academicKpisData);
  }, [detail, academicKpisData, academicKpisLoading, academicKpisError]);

  // Extract academic exam types data
  const academicData = useMemo(() => {
    const result = (
      academicKpisData as { result?: Record<string, unknown> } | undefined
    )?.result;
    return (result?.exam_types as unknown[] | undefined) || [];
  }, [academicKpisData]);

  // Get academic KPI values for the selected exam type
  const academicKpiValues = useMemo(() => {
    const out: Record<string, string> = {};
    const examTypeData = (
      academicData as Array<{
        exam_type?: string;
        pass_pct?: string | number;
        average_mark?: string | number;
        yoy_improvement?: string | number;
        exam_to_exam_pp?: string | number;
        assessment_coverage?: string | number;
        mark_entry_completion?: string | number;
      }>
    ).find((e) => e.exam_type === assessment);

    if (examTypeData) {
      out['Pass Percentage %'] = String(examTypeData.pass_pct ?? '');
      out['Average Mark'] = String(examTypeData.average_mark ?? '');
      out['YoY Improvement'] = String(examTypeData.yoy_improvement ?? '');
      out['Exam to Exam Pass Point'] = String(
        examTypeData.exam_to_exam_pp ?? ''
      );
      out['Assessment Coverage'] = String(
        examTypeData.assessment_coverage ?? ''
      );
      out['Mark Entry Completion'] = String(
        examTypeData.mark_entry_completion ?? ''
      );
    }
    return out;
  }, [academicData, assessment]);

  // The cards with their Enrollment / Attendance / Infrastructure /
  // Scholarship / THIRAN values overlaid from the live APIs (other cards
  // fall through unchanged).

  const displayCards = useMemo(
    () =>
      reviewCards.map((card) => ({
        ...card,
        rows: card.rows.map((row) => {
          const colorByPct = PCT_DOT_CARDS.has(card.title);

          const live =
            card.title === 'Academic Performance'
              ? applyLiveRow(row, academicKpiValues)
              : card.title === 'Scholarship' ||
                  card.title === 'THIRAN' ||
                  card.title === 'SMC' ||
                  card.title === 'Palli Paarvai'
                ? applyLiveRow(row, reviewKpiValues1, colorByPct)
                : applyLiveRow(row, reviewKpiValues, colorByPct);

          // Count rows on these cards get no colour (gray dot)
          return colorByPct && !isPctRow(row)
            ? { ...live, dot: undefined }
            : live;
        }),
      })),
    [reviewKpiValues, reviewKpiValues1, academicKpiValues]
  );

  // Table view (DataTable paginator)
  const [tableFirst, setTableFirst] = useState(0);
  const [tableRows, setTableRows] = useState(10);

  // ── Drill-down (shared by EVERY card). `detail` itself is declared earlier
  // (above the card-tile queries, which need to skip on it). ──
  const [drill, setDrill] = useState<{
    district: string | null;
    block: string | null;
  }>(() => ({
    district: headerDistrict || null,
    block: headerBlock || null,
  }));
  const [detailFirst, setDetailFirst] = useState(0);
  const [detailRows, setDetailRows] = useState(10);
  // KPI columns for district & block tables ([] = All KPIs)
  const [colSel, setColSel] = useState<string[]>([]);
  // KPI columns for the school table ([] = All KPIs)
  const [schoolSel, setSchoolSel] = useState<string[]>([]);
  const [kpiOptions, setKpiOptions] = useState<KpiOption[]>([]);
  const schoolKpiRef = useRef<any>(null);
  // Recognition Order filter: which pending category was clicked
  const [recognitionOrderFilter, setRecognitionOrderFilter] = useState<
    'total' | 'district' | 'state' | 'pending15' | null
  >(null);
  // Palli Paarvai manages its own 6 tabs internally (view_id 1-6) rather than
  // through colSel/detailSchemas like the generic drill cards, so clicking
  // one of its KPI rows needs its own "which tab to open on" signal — bumped
  // on every click (not just the view id) so re-clicking the SAME row while
  // already open still re-selects that tab instead of being a no-op.
  const [palliPaarvaiOpenView, setPalliPaarvaiOpenView] = useState<{
    viewId: number;
    key: number;
  } | null>(null);

  // Academic Performance grid filters (academicdetailtable): exam type
  // (0 All / 1 Quarterly / 2 Half-yearly / 3 Annual) and class range
  // (0: 6-12 / 1: 6-10 / 2: 11-12). Reset whenever the Academic drill re-opens.
  const [academicExamTypeId, setAcademicExamTypeId] = useState<string>('0');
  const [academicClassRange, setAcademicClassRange] = useState<string>('0');
  const academicExamTypeOptions: KpiOption[] = [
    { label: 'All', value: '0' },
    { label: 'Quarterly', value: '1' },
    { label: 'Half-yearly', value: '2' },
    { label: 'Annual', value: '3' },
  ];
  const academicClassRangeOptions: KpiOption[] = [
    { label: 'Classes 6-12', value: '0' },
    { label: 'Classes 6-10', value: '1' },
    { label: 'Classes 11-12', value: '2' },
  ];

  // Detail-table APIs for the drill. Enrollment, Attendance and Infrastructure
  // are each fully API-driven (district → block → school) and share the same
  // id-resolution + args mechanism. Only the matching card's query runs.
  //
  // district_id / block_id are resolved from the names in the most recently
  // loaded items (kept in refs so a parent level's id map survives after we
  // drill into it): district_id lets the API return that district's blocks;
  // block_id its schools. One pair of refs is shared because only one of these
  // cards is open at a time and opening one resets the drill to district level,
  // which repopulates the maps from that card's own response.
  const liveDistIdRef = useRef<Record<string, string>>({});
  const liveBlockIdRef = useRef<Record<string, string>>({});
  const liveDrillIds = useMemo(() => {
    const out: { district_id?: string; block_id?: string } = {};
    const dKey = normName(drill.district);
    const bKey = normName(drill.block);
    if (dKey && liveDistIdRef.current[dKey]) {
      out.district_id = liveDistIdRef.current[dKey];
    }
    if (bKey && liveBlockIdRef.current[bKey]) {
      out.block_id = liveBlockIdRef.current[bKey];
    }
    return out;
  }, [drill.district, drill.block]);
  const liveTableArgs = useMemo(
    () => ({ ...reviewFilterArgs, ...liveDrillIds }),
    [reviewFilterArgs, liveDrillIds]
  );
  const { data: enrollTableData, error: enrollTableError } =
    useGetReviewEnrollmentTableQuery(liveTableArgs, {
      skip: detail !== 'Enrollment',
    });
  const { data: attendanceTableData, error: attendanceTableError } =
    useGetReviewAttendanceTableQuery(liveTableArgs, {
      skip: detail !== 'Attendance',
    });
  const { data: infraTableData, error: infraTableError } =
    useGetReviewInfraTableQuery(liveTableArgs, {
      skip: detail !== 'Infrastructure',
    });
  const { data: scholarshipTableData, error: scholarshipTableError } =
    useGetReviewScholarshipTableQuery(liveTableArgs, {
      skip: detail !== 'Scholarship',
    });
  const { data: thiranTableData, error: thiranTableError } =
    useGetReviewThiranTableQuery(liveTableArgs, {
      skip: detail !== 'THIRAN',
    });
  const { data: smcTableData, error: smcTableError } =
    useGetReviewSmcTableQuery(liveTableArgs, {
      skip: detail !== 'SMC',
    });
  // Academic Performance grid (academicdetailtable): same drill args plus the
  // exam-type / class-range dropdowns above the grid.
  const academicDetailArgs = useMemo(
    () => ({
      ...liveTableArgs,
      exam_type_id: academicExamTypeId,
      class_range: academicClassRange,
    }),
    [liveTableArgs, academicExamTypeId, academicClassRange]
  );
  const { data: academicDetailData, error: academicDetailError } =
    useGetAcademicDetailTableQuery(academicDetailArgs, {
      skip: detail !== 'Academic Performance',
    });
  // The response for whichever live card is currently open.
  const liveTableData =
    detail === 'Enrollment'
      ? enrollTableData
      : detail === 'Attendance'
        ? attendanceTableData
        : detail === 'Infrastructure'
          ? infraTableData
          : detail === 'Scholarship'
            ? scholarshipTableData
            : detail === 'THIRAN'
              ? thiranTableData
              : detail === 'SMC'
                ? smcTableData
                : detail === 'Academic Performance'
                  ? academicDetailData
                  : undefined;
  // Cache the name → id map for whichever level the API just returned, so the
  // next drill can resolve the id it needs.
  useEffect(() => {
    const result = (
      liveTableData as
        | { result?: { level?: string; items?: Record<string, unknown>[] } }
        | undefined
    )?.result;
    const items = result?.items;
    if (!Array.isArray(items)) return;
    if (result?.level === 'district') {
      const m: Record<string, string> = {};
      items.forEach((it) => {
        if (it?.name != null && it?.district_id != null) {
          m[normName(String(it.name))] = String(it.district_id);
        }
      });
      liveDistIdRef.current = m;
    } else if (result?.level === 'block') {
      const m: Record<string, string> = {};
      items.forEach((it) => {
        if (it?.name != null && it?.block_id != null) {
          m[normName(String(it.name))] = String(it.block_id);
        }
      });
      liveBlockIdRef.current = m;
    }
  }, [liveTableData]);
  useEffect(() => {
    if (detail !== 'Enrollment') return;
    if (enrollTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewenrollmenttable] error', enrollTableError);
    } else if (enrollTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewenrollmenttable] response', enrollTableData);
    }
  }, [detail, enrollTableData, enrollTableError]);
  useEffect(() => {
    if (detail !== 'Attendance') return;
    if (attendanceTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewattendancetable] error', attendanceTableError);
    } else if (attendanceTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewattendancetable] response', attendanceTableData);
    }
  }, [detail, attendanceTableData, attendanceTableError]);
  useEffect(() => {
    if (detail !== 'Infrastructure') return;
    if (infraTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewinfratable] error', infraTableError);
    } else if (infraTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewinfratable] response', infraTableData);
    }
  }, [detail, infraTableData, infraTableError]);
  useEffect(() => {
    if (detail !== 'Scholarship') return;
    if (scholarshipTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewscholarshiptable] error', scholarshipTableError);
    } else if (scholarshipTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewscholarshiptable] response', scholarshipTableData);
    }
  }, [detail, scholarshipTableData, scholarshipTableError]);
  useEffect(() => {
    if (detail !== 'THIRAN') return;
    if (thiranTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewthirantable] error', thiranTableError);
    } else if (thiranTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewthirantable] response', thiranTableData);
    }
  }, [detail, thiranTableData, thiranTableError]);
  useEffect(() => {
    if (detail !== 'SMC') return;
    if (smcTableError) {
      // eslint-disable-next-line no-console
      console.error('[reviewsmctable] error', smcTableError);
    } else if (smcTableData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[reviewsmctable] response', smcTableData);
    }
  }, [detail, smcTableData, smcTableError]);
  useEffect(() => {
    if (detail !== 'Academic Performance') return;
    if (academicDetailError) {
      // eslint-disable-next-line no-console
      console.error('[academicdetailtable] error', academicDetailError);
    } else if (academicDetailData !== undefined) {
      // eslint-disable-next-line no-console
      console.log('[academicdetailtable] response', academicDetailData);
    }
  }, [detail, academicDetailData, academicDetailError]);

  const allKpis = useMemo(
    () =>
      displayCards.flatMap((c) =>
        c.rows.map((row) => ({
          component: c.title,
          color: c.color,
          row,
          rag: ragOf(row.dot),
        }))
      ),
    [displayCards]
  );

  // Load school KPI options only when a school table opens
  useEffect(() => {
    if (!detail || !drill.district || !drill.block) return;
    let alive = true;
    fetchSchoolKpiOptions(detail)
      .then((o) => alive && setKpiOptions(o))
      .catch(() => alive && setKpiOptions(schoolKpiOptionsFor(detail)));
    return () => {
      alive = false;
    };
  }, [detail, drill.district, drill.block]);

  /** Move within the drill path (district → block → school). */
  const goDrill = (district: string | null, block: string | null) => {
    setDrill({ district, block });
    setDetailFirst(0);
    setSchoolSel([]); // school KPI filter resets on every level change
    // Keep the header District / Block dropdowns in sync with the grid drill.
    onGeoSync?.({ district, block });
    // Stepping up or changing level clears the selected school
    onSchoolChange?.(null);
  };

  // Reverse sync: when the header District / Block dropdowns change (e.g. the
  // user clears one), mirror it into the grid drill. The normalized compare
  // means this never fights the grid→header sync above (which canonicalises
  // case); it only fires on a genuine external change, and it uses setDrill
  // directly (not goDrill) so it doesn't echo back to the header.
  useEffect(() => {
    if (!detail) return;
    const d = headerDistrict || null;
    const b = headerBlock || null;
    if (
      normName(drill.district) === normName(d) &&
      normName(drill.block) === normName(b)
    ) {
      return;
    }
    setDrill({ district: d, block: b });
    setDetailFirst(0);
    setSchoolSel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, headerDistrict, headerBlock]);

  /** Open a component's drill-down at the District level. */
  const openDetail = (title: string, kpiLabel?: string) => {
    // Switching cards from WITHIN a drill (e.g. the pills) keeps the current
    // district/block so the newly selected card loads that same level's data
    // instead of snapping back to the district level.
    const switchingWhileDrilled = detail !== null;
    setDetail(title);

    if (title === 'Palli Paarvai') {
      const viewId = kpiLabel ? (PALLI_PAARVAI_KPI_TO_VIEW[kpiLabel] ?? 1) : 1;
      setPalliPaarvaiOpenView((prev) => ({
        viewId,
        key: (prev?.key ?? 0) + 1,
      }));
    }
    // Special case: if clicking on "Student attendance %" from State Summary, go straight to school level
    if (title === 'State Summary' && kpiLabel === 'Student attendance %') {
      // Set to show schools - we'll use a generic district/block to show all schools
      setDrill({ district: 'Tamil Nadu', block: 'All' });
    } else if (!switchingWhileDrilled) {
      setDrill({ district: null, block: null });
    }
    if (title === 'Academic Performance' && !switchingWhileDrilled) {
      setAcademicExamTypeId('0');
      setAcademicClassRange('0');
    }
    setDetailFirst(0);

    // When a specific KPI row is clicked, pre-select it in the detail view.
    // Match the kpiLabel against the schema labels (case-insensitive) to get the correct column.
    if (kpiLabel) {
      // Find the matching column label in the schema (case-insensitive match)
      const schema = detailSchemas[title] ?? [];
      const matchingCol = schema.find(
        (col) => col.label.toLowerCase() === kpiLabel.toLowerCase()
      );
      if (matchingCol) {
        setColSel([matchingCol.label]); // Use the schema label for filtering
      } else {
        // If no exact match in schema, use the provided label directly
        setColSel([kpiLabel]);
      }
    } else {
      const schema = detailSchemas[title] ?? [];
      setColSel(schema.map((col) => col.label)); // Select all KPI labels
    }
    setSchoolSel([]);
    setKpiOptions([]);

    // Handle Recognition Order filtering
    if (title === 'Recognition Order - DPS') {
      if (kpiLabel === 'Total pending applications') {
        setRecognitionOrderFilter('total');
        setColSel(['TOTAL PENDING']);
      } else if (kpiLabel === 'District pending') {
        setRecognitionOrderFilter('district');
        setColSel(['DISTRICT PENDING']);
      } else if (kpiLabel === 'State pending') {
        setRecognitionOrderFilter('state');
        setColSel(['STATE PENDING']);
      } else if (kpiLabel === 'Pending < 15 days') {
        setRecognitionOrderFilter('pending15');
        setColSel(['PENDING < 15 DAYS']);
      } else {
        setRecognitionOrderFilter(null);
      }
    } else {
      setRecognitionOrderFilter(null);
    }
  };

  /** Back to the main Review Dashboard (the cards). */
  const goToDashboard = () => {
    setDetail(null);
    setDrill({ district: null, block: null });
    setDetailFirst(0);
    setColSel([]);
    setSchoolSel([]);
    setKpiOptions([]);
    setRecognitionOrderFilter(null);
    // Notify parent header to clear district/block selections
    onGeoSync?.({ district: null, block: null });
    onSchoolChange?.(null);
  };

  const renderCardGrid = (variant: '' | 'compact' | 'kpi') => {
    // Every card stays visible regardless of the header filters: Management /
    // Category / School Type / district / block all narrow the DATA each
    // card's API call returns (via reviewFilterArgs) rather than hiding whole
    // cards — there's no real per-card programme/management/etc. attribute to
    // filter on, only the underlying KPI values change.
    const filteredCards = displayCards;

    return (
      <div
        className={`review-grid${variant ? ` review-grid--${variant}` : ''}`}
      >
        {filteredCards.length > 0 ? (
          filteredCards.map((card) => (
            <section
              key={card.title}
              className="review-card"
              style={{ borderTopColor: card.color }}
            >
              <h6 className="review-card__title">{card.title}</h6>
              {/* The "no data" check only swaps out the ROWS LIST — it must
                  never hide the Assessment toggle or "View details" button
                  below. Academic Performance's rows are whatever the
                  currently-selected assessment scope (All/Q/H/A) returns, so
                  if THAT scope happens to be all-zero (e.g. a filter combo
                  zeroes "Annual" but Quarterly/Half Yearly still have real
                  data), hiding the toggle here would strand the user on the
                  empty scope with no way back to the ones that have data. */}
              {cardHasNoData(card) ? (
                <div className="review-card__empty">No data available</div>
              ) : (
                <ul className="review-card__rows">
                  {card.rows.map((row) => (
                    <li
                      key={row.label}
                      className="review-row is-clickable"
                      onClick={() => openDetail(card.title, row.label)}
                    >
                      <span
                        className={`review-row__dot is-${row.dot ? row.dot : 'gray'}`}
                      />
                      <span className="review-row__label">{row.label}</span>
                      <span className="review-row__value">
                        <Val row={row} />
                      </span>
                      <i className="pi pi-chevron-right review-row__chevron" />
                    </li>
                  ))}
                </ul>
              )}
              <div className="review-card__footer">
                <Button
                  type="button"
                  link
                  label="View details"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  className="review-card__link"
                  style={{ '--card-color': card.color } as React.CSSProperties}
                  onClick={() => openDetail(card.title)}
                />
                {card.assessmentToggle && variant !== 'compact' && (
                  <div className="review-card__assessment">
                    <span className="review-card__assessment-label">
                      Assessment:
                    </span>
                    <SelectButton
                      value={assessment}
                      options={assessmentScopes}
                      onChange={(e) => e.value && setAssessment(e.value)}
                      className="review-card__assessment-sb"
                    />
                  </div>
                )}
              </div>
            </section>
          ))
        ) : (
          <div className="review-grid__empty">
            <p>No cards match the selected filters</p>
          </div>
        )}
      </div>
    );
  };

  const kpiRows = useMemo(
    () => allKpis.map((k, i) => ({ ...k, sno: i + 1 })),
    [allKpis]
  );

  // ── Table view: PrimeReact DataTable with built-in paginator ──
  const renderTable = () => (
    <div className="review-table-wrap">
      <DataTable
        // value={allKpis}
        value={kpiRows}
        className="review-table"
        paginator
        first={tableFirst}
        rows={tableRows}
        rowsPerPageOptions={ROWS_PER_PAGE}
        onPage={(e) => {
          setTableFirst(e.first);
          setTableRows(e.rows);
        }}
        onRowClick={(e) => openDetail(e.data.component)}
        rowClassName={() => 'review-table__row'}
      >
        <Column
          field="sno"
          header="S.NO"
          className="is-right rt-sno"
          headerClassName="is-right"
          style={{ width: '70px' }}
        />
        <Column
          header="COMPONENT"
          body={(k) => (
            <>
              <span className="rt-dot" style={{ background: k.color }} />
              {k.component}
            </>
          )}
        />
        <Column header="KPI" className="rt-kpi" body={(k) => k.row.label} />
        <Column
          header="VALUE"
          className="is-right rt-value"
          headerClassName="is-right"
          body={(k) => <Val row={k.row} />}
        />
        <Column
          header="STATUS"
          body={(k) => (
            <span
              className="rt-status"
              style={{
                color:
                  k.rag === 'none' ? undefined : ragMeta[k.rag as Rag].color,
              }}
            >
              {k.rag === 'none' ? '—' : ragMeta[k.rag as Rag].label}
            </span>
          )}
        />
        <Column
          className="is-right"
          body={() => <i className="pi pi-chevron-right rt-chevron" />}
        />
      </DataTable>
    </div>
  );

  // ── Accordion view: PrimeReact Accordion ──
  const renderAccordion = () => (
    <Accordion
      className="review-accordion"
      activeIndex={openIdx}
      onTabChange={(e) => setOpenIdx(e.index as number | null)}
    >
      {displayCards.map((card) => {
        const counts = {
          critical: card.rows.filter((r) => ragOf(r.dot) === 'critical').length,
          watch: card.rows.filter((r) => ragOf(r.dot) === 'watch').length,
          good: card.rows.filter((r) => ragOf(r.dot) === 'good').length,
        };
        return (
          <AccordionTab
            key={card.title}
            header={
              <span className="review-acc__head">
                <span
                  className="review-acc__dot"
                  style={{ background: card.color }}
                />
                <span className="review-acc__title">{card.title}</span>
                <span className="review-acc__badges">
                  <span className="review-acc__badge is-critical">
                    {counts.critical}
                  </span>
                  <span className="review-acc__badge is-watch">
                    {counts.watch}
                  </span>
                  <span className="review-acc__badge is-good">
                    {counts.good}
                  </span>
                </span>
              </span>
            }
          >
            <ul className="review-card__rows">
              {card.rows.map((row) => (
                <li
                  key={row.label}
                  className="review-row is-clickable"
                  onClick={() => openDetail(card.title)}
                >
                  <span className={`review-row__dot is-${row.dot ?? 'gray'}`} />
                  <span className="review-row__label">{row.label}</span>
                  <span className="review-row__value">
                    <Val row={row} />
                  </span>
                  <i className="pi pi-chevron-right review-row__chevron" />
                </li>
              ))}
            </ul>
            <div className="review-acc__foot">
              <Button
                type="button"
                link
                label="View details"
                icon="pi pi-arrow-right"
                iconPos="right"
                className="review-card__link"
                style={
                  {
                    '--card-color': card.color,
                    '--card-color-hover': card.color + 'dd',
                  } as React.CSSProperties
                }
                onClick={() => openDetail(card.title)}
              />
            </div>
          </AccordionTab>
        );
      })}
    </Accordion>
  );

  const renderStatusBoard = () => (
    <div className="review-status">
      {(['critical', 'watch', 'good', 'none'] as Rag[]).map((rag) => {
        const items = allKpis.filter((k) => k.rag === rag);
        const meta = ragMeta[rag];
        return (
          <div className="review-status__col" key={rag}>
            <div
              className="review-status__head"
              style={{ borderTopColor: meta.color }}
            >
              <i className={meta.icon} style={{ color: meta.color }} />
              <span className="review-status__label">{meta.label}</span>
              <span className="review-status__count">{items.length}</span>
            </div>
            <div className="review-status__body">
              {items.map((k, i) => (
                <div
                  className="review-status__item"
                  key={`${k.row.label}-${i}`}
                  onClick={() => openDetail(k.component)}
                  style={{ cursor: 'pointer' }}
                >
                  <span
                    className="review-status__item-comp"
                    style={{ color: k.color }}
                  >
                    {k.component.toUpperCase()}
                  </span>
                  <span className="review-status__item-kpi">{k.row.label}</span>
                  <span className="review-status__item-val">
                    <Val row={k.row} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderHeatmap = () => (
    <div className="review-heatmap">
      {displayCards.map((card) => (
        <div className="review-heatmap__row" key={card.title}>
          <div
            className="review-heatmap__label"
            style={{ borderLeftColor: card.color }}
          >
            {card.title}
          </div>
          <div className="review-heatmap__cells">
            {card.rows.map((row) => {
              const rag = ragOf(row.dot);
              return (
                <div
                  key={row.label}
                  className={`review-heatmap__cell is-${rag}`}
                  title={`${card.title} — ${row.label}`}
                  onClick={() => openDetail(card.title)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="review-heatmap__cell-val">
                    <Val row={row} />
                  </span>
                  <span className="review-heatmap__cell-label">
                    {row.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // Component pills shared by the drill-down (PrimeReact Buttons)
  const renderPills = (active: string) => (
    <div className="review-detail__pills">
      {displayCards.map((c) => (
        <Button
          key={c.title}
          type="button"
          text
          label={shortOf(c)}
          icon={<FontAwesomeIcon icon={c.icon} />}
          className={`review-detail__pill${c.title === active ? ' is-active' : ''}`}
          onClick={() => openDetail(c.title)}
        />
      ))}
    </div>
  );

  // ───────────────────────────────────────────────────────────
  // Generic drill-down: District → Block → School (all components)
  // ───────────────────────────────────────────────────────────
  const renderDrill = () => {
    const card = displayCards.find((c) => c.title === detail);
    if (!card) return null;

    // Palli Paarvai has its own six-tab detail panel (pp_details), each tab a
    // different shape with its own drills — handled by a dedicated component
    // instead of the generic District → Block → School table below.
    if (card.title === 'Palli Paarvai') {
      return (
        <div className="review-detail">
          <div className="review-detail__bar">
            <Button
              type="button"
              text
              icon="pi pi-arrow-left"
              label="All Components"
              className="review-detail__back"
              onClick={goToDashboard}
            />
            <span className="review-detail__crumb">
              <FontAwesomeIcon icon={faLayerGroup} />
              <strong>{card.title}</strong>
            </span>
          </div>
          {renderPills(card.title)}
          <PalliPaarvaiDetail
            baseFilters={reviewFilterArgs}
            acdYr={currentAcdYr}
            openView={palliPaarvaiOpenView}
            onViewChange={setPalliPaarvaiViewId}
            school={headerSchool}
            onSchoolChange={onSchoolChange}
            // Header district/block NAMES (baseFilters only carries the ids) —
            // view 2's drill is now header-driven, so it needs the current
            // district/block names for its breadcrumb + to pass back up on a
            // block/school row click.
            headerDistrict={headerDistrict}
            headerBlock={headerBlock}
            districtLocked={districtLocked}
            blockLocked={blockLocked}
            // Straight through to Dashboard.tsx's handleGeoSelect (via
            // onGeoSelect), which sets district + block + school together —
            // unlike onGeoSync, it doesn't drop `school`. Same direct
            // wiring Caution.tsx / InfrastructureCaution.tsx already use.
            onGeoSelect={onGeoSelect}
            onScopeOptions={onScopeOptions}
          />
        </div>
      );
    }

    // Special handling for Recognition Order - show applications at district level
    const isRecognitionOrder = card.title === 'Recognition Order - DPS';
    const level: 'district' | 'block' | 'school' | 'applications' =
      !drill.district
        ? 'district'
        : isRecognitionOrder && drill.district && !drill.block
          ? 'applications'
          : !drill.block
            ? 'block'
            : 'school';

    // Use applications schema for Recognition Order when showing applications
    const schemaTitleOverride =
      isRecognitionOrder && level === 'applications'
        ? 'Recognition Order - DPS [applications]'
        : card.title;

    const allCols = detailSchemas[schemaTitleOverride] ?? [];
    const cols = colSel.length
      ? allCols.filter((c) => colSel.includes(c.label))
      : allCols;

    // ── Enrollment / Attendance / Infrastructure / Scholarship / THIRAN are
    // live (district → block → school) from their review*table APIs. Rows +
    // footers come straight from the API items, mapped by each column's
    // apiKey; the generic table below renders them at every level. Footers sum
    // counts and average rates (via `aggregate`). ──
    const isLive =
      card.title === 'Enrollment' ||
      card.title === 'Attendance' ||
      card.title === 'Infrastructure' ||
      card.title === 'Scholarship' ||
      card.title === 'THIRAN' ||
      card.title === 'SMC' ||
      card.title === 'Academic Performance';
    const liveResult = isLive
      ? (
          liveTableData as
            | {
                result?: {
                  level?: string;
                  items?: Record<string, unknown>[];
                  grand_total?: Record<string, unknown>;
                };
              }
            | undefined
        )?.result
      : undefined;
    const liveItems: Record<string, unknown>[] = Array.isArray(
      liveResult?.items
    )
      ? (liveResult!.items as Record<string, unknown>[])
      : [];
    // grand_total is provided by academicdetailtable (and potentially other
    // live APIs). Use it directly as the footer row instead of re-aggregating
    // client-side, which would average rates incorrectly.
    const grandTotal = liveResult?.grand_total;
    // const liveRows: DRow[] = liveItems.map((it) => {
    //   const r: DRow = { name: String(it.name ?? '') };
    //   cols.forEach((c, j) => {
    //     const raw = c.apiKey ? it[c.apiKey] : undefined;
    //     r[`k${j}`] = raw != null && raw !== '' ? Number(raw) || 0 : 0;
    //   });
    //   return r;
    // });
    const liveRows: DRow[] = liveItems
      .filter((it) => it.name != null && String(it.name).trim() !== '')
      .map((it, i) => {
        const r: DRow = { name: String(it.name), _key: `${it.name}-${i}` };
        console.log(cols, 'cols');
        cols.forEach((c, j) => {
          const raw = c.apiKey ? it[c.apiKey] : undefined;
          r[`k${j}`] = raw != null && raw !== '' ? Number(raw) || 0 : 0;
        });
        return r;
      });
    const liveFooters = cols.map((c, j) => {
      // If the API returned a grand_total, use it directly for this column.
      // This is more accurate than client-side aggregation (e.g. for Academic
      // Performance where all columns are rates, averaging them is wrong).
      if (grandTotal && c.apiKey) {
        const raw = grandTotal[c.apiKey];
        if (raw != null && raw !== '') {
          return fmtDetail(c, Number(raw) || 0);
        }
      }
      return c.apiKey == 'transition_not_meeting_100_pct' ||
        c.apiKey == 'student_attendance_pct' ||
        c.apiKey == 'teacher_attendance_pct' ||
        c.apiKey == 'school_non_mark_pct' ||
        c.apiKey == 'pct_of_enrolment' ||
        c.apiKey == 'blo_attainment_pct' ||
        c.apiKey == 'baseline_update_pct' ||
        c.apiKey == 'payment_pending_pct' ||
        c.apiKey == 'payment_failed_pct' ||
        c.apiKey == 'payment_success_pct' ||
        c.apiKey == 'closure_rate'
        ? groupAggregate(c, liveItems)
        : aggregate(
            c,
            liveRows.map((r) => r[`k${j}`])
          );
    });
    // const liveFooters = cols.map((c, j) => {
    //   const vals = liveRows.map((r) => r[`k${j}`]);
    //   const footer = aggregate(c, vals);

    //   console.log('[liveFooters]', {
    //     j,
    //     label: c.label,
    //     kind: c.kind,
    //     apiKey: c.apiKey,
    //     vals,
    //     footer,
    //   });

    //   return footer;
    // });
    const liveNameHeader =
      liveResult?.level === 'school'
        ? 'SCHOOL'
        : liveResult?.level === 'block'
          ? 'BLOCK'
          : 'DISTRICT';
    // At the school level the API switches to per-school records (a shared
    // school profile: students / teachers / PTR / dropouts), so live cards
    // render the rich school table from these instead of the aggregate
    // district/block columns.
    const isPalli = card.title === 'Palli Paarvai';
    const isTextCol = (col: DCol) => col.kind === 'text';
    const alignCls = (col: DCol) => (isTextCol(col) ? 'is-left' : 'is-right');

    // ── Per-section SCHOOL-level columns. Columns come from
    // SCHOOL_METRICS[card.title] so each section's school table matches its own
    // tab instead of sharing one set. ──
    const schoolMetrics =
      SCHOOL_METRICS[card.title] ?? ENROLLMENT_SCHOOL_METRICS;
    const visibleSchoolMetrics = schoolSel.length
      ? schoolMetrics.filter((c) => schoolSel.includes(c.label))
      : schoolMetrics;
    const schoolOptions = schoolMetrics.map((c) => ({
      label: c.label,
      value: c.label,
    }));

    // Build KPI options for district/block level - combine card rows and schema
    // const kpiOptionsDistrictBlock = Array.from(
    //   new Set([
    //     ...card.rows.map((r) => r.label),
    //     ...allCols.map((c) => c.label),
    //   ])
    // ).map((label) => ({
    //   label,
    //   value: label,
    // }));

    const kpiOptionsDistrictBlock = allCols.map((c) => ({
      label: c.label,
      value: c.label,
    }));

    // ── rows for the District / Block / Applications level (DataTable reads k0, k1, ...) ──
    const dRows: DRow[] =
      level === 'district'
        ? card.title === 'Recognition Order - DPS'
          ? // Recognition Order: use real data from makeRecognitionOrderRows
            makeRecognitionOrderRows('Tamil Nadu', DISTRICTS.length).map(
              (row, i) => {
                const r: DRow = { name: DISTRICTS[i] ?? `District ${i + 1}` };
                cols.forEach((c, j) => {
                  // Map Recognition Order fields to column labels
                  if (c.label === 'DISTRICT') r[`k${j}`] = r.name;
                  else if (c.label === 'TOTAL PENDING')
                    r[`k${j}`] = fmtIN(row.totalPending);
                  else if (c.label === 'DISTRICT PENDING')
                    r[`k${j}`] = fmtIN(row.districtPending);
                  else if (c.label === 'STATE PENDING')
                    r[`k${j}`] = fmtIN(row.statePending);
                  else if (c.label === 'PENDING < 15 DAYS')
                    r[`k${j}`] = fmtIN(row.pending15);
                  else if (c.label === 'PENDING 15-30 DAYS')
                    r[`k${j}`] = fmtIN(row.pending1530);
                  else if (c.label === 'PENDING > 30 DAYS')
                    r[`k${j}`] = fmtIN(row.pendingOver30);
                  else if (c.label === 'AVG PENDING DAYS')
                    r[`k${j}`] = row.avgPendingDays;
                  else r[`k${j}`] = detailCell(c, i, allCols.indexOf(c));
                });
                return r;
              }
            )
          : DISTRICTS.map((d, i) => {
              const r: DRow = { name: d };
              cols.forEach((c, j) => {
                r[`k${j}`] = detailCell(c, i, allCols.indexOf(c));
              });
              return r;
            })
        : level === 'applications'
          ? // Recognition Order applications - show application details
            makeRecognitionOrderApplications(
              drill.district!,
              recognitionOrderFilter as any
            ).map((app) => {
              const r: DRow = { name: app.applicationId };
              const appSchema =
                detailSchemas['Recognition Order - DPS [applications]'] ?? [];
              cols.forEach((c, j) => {
                if (c.label === 'APPLICATION ID')
                  r[`k${j}`] = app.applicationId;
                else if (c.label === 'BLOCK') r[`k${j}`] = app.block;
                else if (c.label === 'UDISE CODE') r[`k${j}`] = app.udiseCode;
                else if (c.label === 'SCHOOL TYPE') r[`k${j}`] = app.schoolType;
                else if (c.label === 'PENDING WITH')
                  r[`k${j}`] = app.pendingWith;
                else if (c.label === 'LEVEL') r[`k${j}`] = app.pendingWith;
                else if (c.label === 'STATUS') r[`k${j}`] = app.status;
                else if (c.label === 'PENDING DAYS')
                  r[`k${j}`] = app.pendingDays;
                else r[`k${j}`] = '';
              });
              return r;
            })
          : level === 'block'
            ? makeBlocks(drill.district!).map((b, bi) => {
                const r: DRow = { name: b };
                cols.forEach((c, j) => {
                  r[`k${j}`] = blockCell(
                    c,
                    bi,
                    allCols.indexOf(c),
                    drill.district!
                  );
                });
                return r;
              })
            : [];

    const blockTotals =
      level === 'block' || level === 'applications'
        ? cols.map((c, j) =>
            aggregate(
              c,
              dRows.map((r) => r[`k${j}`])
            )
          )
        : [];

    // Live cards use the API rows at every level; everything else keeps the
    // generated rows.
    const finalRows = isLive ? liveRows : dRows;

    // School rows: identity fields + each metric value keyed by apiKey. Live
    // cards read the API item (placeholder per-cell when a field is missing);
    // other cards are fully placeholder-filled. Only built at the school level.
    // Hide rows where every selected KPI value is 0
    // keep only rows where at least one visible KPI value is greater than 0
    const displayRows =
      isLive && level !== 'school'
        ? finalRows.filter((r) =>
            cols.some((c, j) => c.kind !== 'text' && Number(r[`k${j}`]) > 0)
          )
        : finalRows;
    const noData =
      isLive &&
      level !== 'school' &&
      level == 'block' &&
      finalRows.length > 0 &&
      displayRows.length === 0 &&
      finalRows.every((r) =>
        cols.every((c, j) => c.kind === 'text' || Number(r[`k${j}`] || 0) === 0)
      );
    const liveIsSchoolLevel = isLive && liveResult?.level === 'school';
    const schoolRecs: SchoolRec[] =
      level !== 'school'
        ? []
        : liveIsSchoolLevel
          ? liveItems.map((it, i) => {
              const sd =
                Number(it.school_id) || seed(String(it.udise_code ?? i));
              const rec: SchoolRec = {
                sno: i + 1,
                district: String(it.district_name ?? drill.district ?? ''),
                block: String(it.block_name ?? drill.block ?? ''),
                udise: String(it.udise_code ?? ''),
                // Most review*table APIs name the school `school_name` and its
                // category `school_category`; academicdetailtable uses the
                // shared `name` field (same key as district/block rows) and
                // `cate_type` instead — fall back to those.
                name: String(it.school_name ?? it.name ?? ''),
                category: String(it.school_category ?? it.cate_type ?? ''),
                _seed: sd,
              };
              schoolMetrics.forEach((c, j) => {
                const raw = it[c.apiKey];
                rec[c.apiKey] =
                  raw != null && raw !== ''
                    ? c.kind === 'text' || c.kind === 'yn'
                      ? String(raw)
                      : toNum(raw)
                    : dummySchoolMetricVal(c, sd, j);
              });
              return rec;
            })
          : makeSchools(drill.district!, drill.block!).map((s, i) => {
              const rec: SchoolRec = {
                sno: i + 1,
                district: s.district,
                block: s.block,
                udise: s.udise,
                name: s.name,
                category: s.category,
                _seed: s.seedN,
              };
              schoolMetrics.forEach((c, j) => {
                rec[c.apiKey] = dummySchoolMetricVal(c, s.seedN, j);
              });
              return rec;
            });

    const norm = (v: string) => v.trim().toLowerCase();
    const displayedSchoolRecs = headerSchool
      ? (() => {
          const targetName = norm(headerSchool);
          const matched = schoolRecs.filter(
            (r) =>
              norm(r.name) === targetName ||
              (headerSchoolUdise && r.udise === headerSchoolUdise)
          );
          return matched.length > 0 ? matched : schoolRecs;
        })()
      : schoolRecs;

    const title =
      level === 'district'
        ? card.title === 'Recognition Order - DPS' && recognitionOrderFilter
          ? `${recognitionOrderFilter === 'total' ? 'Total Pending' : recognitionOrderFilter === 'district' ? 'District Pending' : recognitionOrderFilter === 'pending15' ? 'Pending < 15 Days' : 'State Pending'} Applications — Districts in Tamil Nadu`
          : 'Districts — Tamil Nadu'
        : level === 'applications'
          ? `${recognitionOrderFilter === 'total' ? 'Total Pending' : recognitionOrderFilter === 'district' ? 'District Pending' : recognitionOrderFilter === 'pending15' ? 'Pending < 15 Days' : 'State Pending'} Applications — ${drill.district}`
          : level === 'block'
            ? `Blocks — ${drill.district}`
            : `Schools — ${drill.block}, ${drill.district}`;
    const hint =
      level === 'district'
        ? card.title === 'Recognition Order - DPS' && recognitionOrderFilter
          ? `Showing ${recognitionOrderFilter === 'total' ? 'total' : recognitionOrderFilter === 'district' ? 'district' : recognitionOrderFilter === 'pending15' ? 'pending < 15 days' : 'state'} pending applications by district`
          : 'click a row to open that district'
        : level === 'applications'
          ? 'Application records with block, UDISE code, school type, and pending status'
          : level === 'block'
            ? 'click a row to open that block'
            : '';

    // Back button: always returns to the Review Dashboard cards
    const isSingleSchoolSelected = level === 'school' && !!headerSchool;
    const backLink =
      level === 'school'
        ? isSingleSchoolSelected
          ? {
              label: `Back to ${drill.block} — Schools`,
              go: () => onSchoolChange?.(null),
            }
          : {
              label: `Back to ${drill.district} — Blocks`,
              go: () => goDrill(drill.district, null), // school → blocks
            }
        : level === 'applications'
          ? {
              label: 'Back to Tamil Nadu — Districts',
              go: () => goDrill(null, null), // applications → districts
            }
          : level === 'block'
            ? {
                label: 'Back to Tamil Nadu — Districts',
                go: () => goDrill(null, null), // blocks → districts
              }
            : {
                label: 'Districts in Tamil Nadu',
                go: () => goDrill(null, null), // district level
              };

    // District/block-locked users must never see a "go up" link that would
    // clear their fixed district (district-locked) or fixed block
    // (block-locked) — that would surface districts/blocks outside what
    // they're permitted to see. Each is only a concern when the link in
    // question would ACTUALLY escape that level:
    //   - "Back to Tamil Nadu — Districts" / "Districts in Tamil Nadu"
    //     (level block/applications) clears the district → hidden whenever
    //     districtLocked (true for both district- and block-level users).
    //   - "Back to <district> — Blocks" (level school, no single school)
    //     clears the block → hidden only for block-locked users; a
    //     district-locked user is still allowed to browse blocks within
    //     their own district.
    //   - "Back to <block> — Schools" (single school selected) and the
    //     district-level default only ever stay within the locked scope, so
    //     they're never hidden.
    const backLinkEscapesLock =
      (districtLocked && (level === 'block' || level === 'applications')) ||
      (blockLocked && level === 'school' && !isSingleSchoolSelected);
    // The "Tamil Nadu" home shortcut always clears BOTH district and block,
    // so it's hidden for any locked user.
    const canShowHomeLink = !districtLocked && !blockLocked;

    const onPage = (e: { first: number; rows: number }) => {
      setDetailFirst(e.first);
      setDetailRows(e.rows);
    };

    const tableKey = `${level}-${drill.district}-${drill.block}`; // resets sort on level change

    return (
      <div className="review-detail">
        <div className="review-detail__bar">
          <Button
            type="button"
            text
            icon="pi pi-arrow-left"
            label="All Components"
            className="review-detail__back"
            onClick={goToDashboard}
          />
          <span className="review-detail__crumb">
            <FontAwesomeIcon icon={faLayerGroup} />
            <strong>{card.title}</strong>
            {card.title === 'State Summary' && level === 'school' ? (
              <>
                {' - '}
                <strong>Student attendance % — Districts in Tamil Nadu</strong>
              </>
            ) : (
              <>
                {' - '}
                {drill.district && (
                  <>
                    {drill.block ? (
                      <strong> Schools in {drill.district}</strong>
                    ) : (
                      <strong> Blocks in {drill.district}</strong>
                    )}
                  </>
                )}
                {drill.block && <strong>{drill.block}</strong>}
                {headerSchool && (
                  <>
                    {' > '}
                    <strong>{headerSchool}</strong>
                  </>
                )}
              </>
            )}
            {!backLinkEscapesLock && (
              <Button
                type="button"
                text
                size="small"
                className="review-detail__backlink"
                icon={
                  level !== 'district' ? (
                    <FontAwesomeIcon icon={faTurnUp} />
                  ) : undefined
                }
                label={backLink.label}
                onClick={backLink.go}
              />
            )}
            {canShowHomeLink && (drill.district || drill.block) && (
              <Button
                type="button"
                text
                size="small"
                className="review-detail__backlink"
                icon="pi pi-home"
                label="Tamil Nadu"
                onClick={() => goDrill(null, null)}
              />
            )}
          </span>
        </div>

        {renderPills(card.title)}

        {card.title === 'Academic Performance' && (
          <div className="review-detail__academic-filters">
            <span className="review-detail__academic-filter">
              <label className="review-detail__academic-filter-label">
                Assessment
              </label>
              <Dropdown
                value={academicExamTypeId}
                options={academicExamTypeOptions}
                onChange={(e) => {
                  setAcademicExamTypeId(e.value);
                  setDetailFirst(0);
                }}
                className="review-detail__academic-dd"
              />
            </span>
            <span className="review-detail__academic-filter">
              <label className="review-detail__academic-filter-label">
                Class
              </label>
              <Dropdown
                value={academicClassRange}
                options={academicClassRangeOptions}
                onChange={(e) => {
                  setAcademicClassRange(e.value);
                  setDetailFirst(0);
                }}
                className="review-detail__academic-dd"
              />
            </span>
          </div>
        )}

        <div className="review-detail__tablecard">
          <div className="review-detail__tabletop">
            <span className="review-detail__tabletitle">
              <FontAwesomeIcon icon={faTableList} /> {title}
              <span className="review-detail__hint">{hint}</span>
            </span>
            <div className="review-detail__tools">
              <span
                aria-label="Filter"
                className={`review-detail__filter${(level === 'school' ? schoolSel.length : colSel.length) ? ' is-active' : ''}`}
                style={{ cursor: 'default' }}
              >
                <FontAwesomeIcon icon={faFilter} />
              </span>

              {level === 'school' ? (
                <>
                  <MultiSelect
                    ref={schoolKpiRef}
                    value={schoolSel}
                    options={schoolOptions}
                    onChange={(e) => {
                      const first =
                        schoolSel.length === 0 && e.value.length > 0;
                      setSchoolSel(e.value);
                      setDetailFirst(0);
                      // close the popup on the first selection only
                      if (first) {
                        setTimeout(() => schoolKpiRef.current?.hide(), 0);
                      }
                    }}
                    placeholder="All KPIs"
                    maxSelectedLabels={1}
                    selectedItemsLabel="{0} items selected"
                    className="review-detail__kpidd"
                    panelClassName="review-detail__kpidd-panel"
                    display="chip"
                    scrollHeight="260px"
                  />
                  {schoolSel.length > 0 && (
                    <Button
                      type="button"
                      outlined
                      size="small"
                      icon="pi pi-replay"
                      label="Clear"
                      className="review-detail__clearkpi"
                      onClick={() => {
                        setSchoolSel([]);
                        setDetailFirst(0);
                      }}
                    />
                  )}
                </>
              ) : isPalli ? (
                <Button
                  type="button"
                  outlined
                  size="small"
                  icon="pi pi-arrow-left"
                  label="Back to all KPIs"
                  className="review-detail__backkpi"
                  onClick={() => setColSel([])}
                />
              ) : (
                <>
                  <MultiSelect
                    value={colSel}
                    options={kpiOptionsDistrictBlock}
                    onChange={(e) => {
                      setColSel(e.value);
                      setDetailFirst(0);
                    }}
                    placeholder="All KPIs"
                    maxSelectedLabels={1}
                    selectedItemsLabel="{0} items selected"
                    className="review-detail__kpidd"
                    panelClassName="review-detail__kpidd-panel"
                    display="chip"
                    scrollHeight="200px"
                    itemTemplate={(option) => (
                      <span className="review-detail__kpidd-item-text">
                        {option.label}
                      </span>
                    )}
                  />
                  {colSel.length > 0 && (
                    <Button
                      type="button"
                      outlined
                      size="small"
                      icon="pi pi-replay"
                      label="Clear"
                      className="review-detail__clearkpi"
                      onClick={() => {
                        setColSel([]);
                        setDetailFirst(0);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <div className="review-detail__scroll">
            {noData ? (
              <div className="review-detail__empty">No data available</div>
            ) : level !== 'school' ? (
              // ── District / Block table (live cards render their aggregate
              // district/block columns here, driven by their review*table
              // items; the school level falls through to the rich table). ──
              <DataTable
                key={tableKey}
                // value={finalRows}
                value={displayRows}
                className={`review-detail__table ${level === 'applications' ? 'review-detail__table--applications' : ''}`}
                dataKey="name"
                paginator
                first={detailFirst}
                rows={detailRows}
                rowsPerPageOptions={ROWS_PER_PAGE}
                onPage={onPage}
                onRowClick={(e) => {
                  if (isLive) {
                    // district → blocks, block → schools, school = leaf.
                    if (liveResult?.level === 'district') {
                      goDrill(e.data.name as string, null);
                    } else if (liveResult?.level === 'block') {
                      goDrill(drill.district, e.data.name as string);
                    }
                    return;
                  }
                  if (level === 'district') {
                    goDrill(e.data.name as string, null);
                  } else if (level === 'applications') {
                    // Don't drill into applications rows
                  } else {
                    goDrill(drill.district, e.data.name as string);
                  }
                }}
                rowClassName={() => 'review-detail__table-row'}
                scrollable={true}
              >
                <Column
                  field="sno"
                  header={'S.No'}
                  className="is-right"
                  headerClassName="rt-sno"
                  body={(_r, options) => options.rowIndex + 1}
                  style={{ width: '30px' }}
                />
                <Column
                  field="name"
                  header={
                    isLive
                      ? liveNameHeader
                      : level === 'district'
                        ? 'DISTRICT'
                        : level === 'applications'
                          ? 'APPLICATION ID'
                          : 'BLOCK'
                  }
                  // sortable
                  className="rd-district is-left"
                  headerClassName="is-left"
                  footerClassName="is-left"
                  footer={`Grand Total (${
                    isLive
                      ? displayRows.length
                      : // finalrows.length
                        level === 'district'
                        ? DISTRICTS.length
                        : level === 'applications'
                          ? dRows.length + ' applications'
                          : dRows.length
                  })`}
                />
                {cols.map((col, j) => (
                  <Column
                    key={col.label}
                    field={`k${j}`}
                    header={col.label}
                    sortable
                    className={alignCls(col)}
                    headerClassName={alignCls(col)}
                    footerClassName={alignCls(col)}
                    body={(r: DRow) => (
                      <span className={detailCellClass(col, r[`k${j}`])}>
                        {fmtDetail(col, r[`k${j}`])}
                      </span>
                    )}
                    footer={
                      isLive
                        ? liveFooters[j]
                        : level === 'district'
                          ? col.total
                          : blockTotals[j]
                    }
                  />
                ))}
              </DataTable>
            ) : (
              // ── School table (per-section columns) ──
              <DataTable
                key={tableKey}
                value={displayedSchoolRecs}
                className="review-detail__table review-detail__table--schools"
                dataKey="udise"
                paginator
                first={detailFirst}
                rows={detailRows}
                rowsPerPageOptions={ROWS_PER_PAGE}
                onPage={onPage}
                rowClassName={() => 'review-detail__table-row'}
                emptyMessage="No schools found"
                scrollable={true}
                onRowClick={(e) => {
                  const schoolName = String(e.data?.name ?? '');
                  const udise = String(e.data?.udise ?? '');
                  if (schoolName) {
                    onSchoolSelect?.({
                      name: schoolName,
                      udise,
                      cardTitle: card.title,
                    });
                  }
                }}
                footerColumnGroup={
                  <ColumnGroup>
                    <Row>
                      <Column
                        footer={`Grand Total (${displayedSchoolRecs.length} ${displayedSchoolRecs.length === 1 ? 'school' : 'schools'})`}
                        colSpan={6}
                        footerClassName="is-left"
                      />
                      {visibleSchoolMetrics.map((c) => (
                        <Column
                          key={c.label}
                          footer={aggSchoolMetric(
                            c,
                            displayedSchoolRecs.map((r) => r[c.apiKey])
                          )}
                          footerClassName="is-right"
                        />
                      ))}
                    </Row>
                  </ColumnGroup>
                }
              >
                <Column
                  field="sno"
                  header={
                    <>
                      S.
                      <br />
                      NO
                    </>
                  }
                  className="is-right"
                  headerClassName="is-right"
                />
                <Column
                  field="district"
                  header="DISTRICT"
                  className="is-left"
                  headerClassName="is-left"
                />
                <Column
                  field="block"
                  header="BLOCK"
                  className="is-left"
                  headerClassName="is-left"
                />
                <Column
                  field="udise"
                  header={
                    <>
                      UDISE
                      <br />
                      CODE
                    </>
                  }
                  className="is-left"
                  headerClassName="is-left"
                />
                <Column
                  field="name"
                  header="SCHOOL NAME"
                  className="is-left rd-school school-name"
                  headerClassName="is-left"
                  body={(r: SchoolRec) => (
                    <span
                      className="rt-link"
                      style={{
                        cursor: 'pointer',
                        color: '#1e1b6e',
                        fontWeight: 700,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSchoolSelect?.({
                          name: r.name,
                          udise: r.udise,
                          cardTitle: card.title,
                        });
                      }}
                      title={`Open ${r.name}`}
                    >
                      {r.name}
                    </span>
                  )}
                />
                <Column
                  field="category"
                  header={
                    <>
                      SCHOOL
                      <br />
                      CATEGORY
                    </>
                  }
                  className="is-left rd-cat"
                  headerClassName="is-left"
                />
                {visibleSchoolMetrics.map((c) => (
                  <Column
                    key={c.label}
                    field={c.apiKey}
                    header={c.label}
                    sortable={c.kind !== 'text' && c.label !== 'Total Students'}
                    sortFunction={
                      c.kind !== 'text' && c.label !== 'Total Students'
                        ? (e) =>
                            [...(e.data as SchoolRec[])].sort(
                              (a, b) =>
                                (toNum(a[c.apiKey]) - toNum(b[c.apiKey])) *
                                (e.order || 1)
                            )
                        : undefined
                    }
                    className={
                      c.label === 'Marked status'
                        ? 'is-center'
                        : c.kind === 'text'
                          ? 'is-left'
                          : 'is-right'
                    }
                    headerClassName={
                      c.label === 'Marked status'
                        ? 'is-center'
                        : c.kind === 'text'
                          ? 'is-left'
                          : 'is-right'
                    }
                    body={(s: SchoolRec) => {
                      const cls = schoolMetricClass(c, s[c.apiKey]);
                      return (
                        <span className={cls ? `rd-pill ${cls}` : ''}>
                          {fmtSchoolMetric(c, s[c.apiKey])}
                        </span>
                      );
                    }}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="review-dash">
      {!detail && (
        <div className="review-dash__tabs dash-section">
          <Button
            type="button"
            text
            icon={<FontAwesomeIcon icon={faLayerGroup} />}
            label="District Review"
            className="review-dash__tab is-active"
          />
        </div>
      )}

      <div className="review-dash__head dash-section">
        <div className="review-dash__titles">
          <h4 className="review-dash__title">State Monitoring &amp; Review</h4>
          <p className="review-dash__crumb">
            Tamil Nadu · 2026-27 · All · State → District → Block → School ·{' '}
            <span className="review-dash__hint">tap any KPI to drill down</span>
          </p>
        </div>
        <div className="review-dash__controls">
          <span className="review-dash__icon-btn" aria-hidden="true">
            <FontAwesomeIcon icon={faWandMagicSparkles} />
          </span>
          {/* <Button
            type="button"
            text
            aria-label="Customize"
            icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            className="review-dash__icon-btn"
          /> */}
          <Dropdown
            value={view}
            options={viewOptions}
            onChange={(e) => {
              setView(e.value);
              setTableFirst(0);
            }}
            className="review-dash__view"
          />
        </div>
      </div>

      {detail ? (
        <div className="dash-section">{renderDrill()}</div>
      ) : (
        <div className="dash-section">
          {view === 'cards' && renderCardGrid('')}
          {view === 'compact' && renderCardGrid('compact')}
          {view === 'kpi' && renderCardGrid('kpi')}
          {view === 'table' && renderTable()}
          {view === 'accordion' && renderAccordion()}
          {view === 'status' && renderStatusBoard()}
          {view === 'heatmap' && renderHeatmap()}
        </div>
      )}
    </div>
  );
};

export default ReviewDashboard;
